/* eslint-disable no-unused-vars */
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { isValidPhoneNumber } from "libphonenumber-js";
import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import AuthService from "../../../api/services/AuthService";
import { fetchWithdrawalSecuritySettings, updateWithdrawalSecuritySettings, verifyAndToggleWithdrawalSetting, sendWithdrawalVerificationOtp } from "../../../api/services/withdrawService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { ProfileContext } from "../../../context/ProfileProvider";
import { countriesList, customStyles } from "../../../utils/CountriesList";
import "../PasskeyPage/passkey.css";
import "./security.css";
import AntiPhishingRemoveVerification from "./AntiPhishingRemoveVerification";
import SecurityLevelOverview from "./SecurityLevelOverview";

function maskEmailForDisplay(raw) {
  if (!raw || typeof raw !== "string") return "you***@email.com";
  const at = raw.indexOf("@");
  if (at <= 0) return "***";
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1);
  const visible = Math.min(3, local.length);
  return `${local.slice(0, visible)}***@${domain}`;
}

const maskEmail = (email) => {
  if (!email) return "";
  const [username, domain] = String(email).split("@");
  if (!domain) return email;
  const masked = username.substring(0, 2) + "***" + username.slice(-1);
  return `${masked}@${domain}`;
};

const maskPhone = (phone) => {
  if (!phone) return "";
  const cleaned = String(phone).replace(/\s/g, "");
  if (cleaned.length < 4) return phone;
  return "****" + cleaned.slice(-4);
};

function isTotpNotConfiguredMessage(message) {
  const m = String(message || "").toLowerCase();
  return m.includes("2fa not configured") || m.includes("setup google authenticator first");
}

function restrictionHoursFromDisableResult(result) {
  const hours = Number(result?.data?.restrictionHours ?? result?.data?.restriction_hours);
  return Number.isFinite(hours) && hours > 0 ? hours : 24;
}

/** Mask IPv4 as `49.36.*.*` for Security Logs row. Empty/Unknown → "". */
const maskIpForDisplay = (raw) => {
  if (raw == null) return "";
  const ip = String(raw).trim();
  if (!ip || ip.toLowerCase() === "unknown" || ip === "null" || ip === "undefined") return "";
  const parts = ip.split(".");
  if (parts.length === 4 && parts.every((p) => /^\d{1,3}$/.test(p))) {
    return `${parts[0]}.${parts[1]}.*.*`;
  }
  return ip;
};

const truthyFlag = (v) => {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v == null) return false;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes" || s === "on") return true;
    if (s === "false" || s === "0" || s === "no" || s === "off" || s === "") return false;
  }
  return Boolean(v);
};

const firstTruthyKey = (obj, keys) => {
  if (!obj || typeof obj !== "object") return false;
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined && obj[k] !== null) {
      if (truthyFlag(obj[k])) return true;
    }
  }
  return false;
};

const normMethodToken = (x) => String(x ?? "").toLowerCase().replace(/[-_\s]+/g, "");

/**
 * Backs like `['totp','email']` or `['TOTP', 'EMAIL']` from check-two-login-2-step.
 */
const parseEnabledMethodsArray = (arr) => {
  if (!Array.isArray(arr) || !arr.length) return null;
  const tokens = arr.map((x) => (typeof x === "string" ? normMethodToken(x) : normMethodToken(x?.method || x?.name || x)));
  return {
    totp: tokens.some(
      (a) =>
        a === "totp" ||
        a === "topt" /* typo */ ||
        a === "ga" ||
        a.includes("google") ||
        a.includes("authenticator")
    ),
    email: tokens.some((a) => a === "email" || a === "mail" || a.includes("email") || a === "e-mail"),
    mobile: tokens.some(
      (a) => a === "mobile" || a === "sms" || a === "phone" || a.includes("sms") || a.includes("phone")
    ),
    passkey: tokens.some(
      (a) => a === "passkey" || a === "webauthn" || a === "fido" || a.includes("passkey")
    ),
  };
};

const flattenOneLevelLogin2Step = (d) => {
  if (!d || typeof d !== "object") return {};
  const nestKeys = [
    "data",
    "login2Step",
    "login2_step",
    "loginTwoStep",
    "login_two_step",
    "loginStep",
    "settings",
    "two_fa",
    "twoFA",
  ];
  let out = { ...d };
  for (const nk of nestKeys) {
    const inner = d[nk];
    if (inner && typeof inner === "object" && !Array.isArray(inner)) {
      out = { ...out, ...inner };
    }
  }
  return out;
};

/**
 * `check-two-login-2-step` may return totp/email/mobile under different names, nested, or as method arrays.
 * Account 2FA (userDetails) is different — this only drives login-2-step toggles.
 */
const normalizeCheckTwoLogin2StepPayload = (raw) => {
  if (!raw) return { totp: false, email: false, mobile: false, passkey: false };
  let d = raw;
  if (d && typeof d === "object" && d.data && typeof d.data === "object") {
    const inner = d.data;
    if (
      Object.prototype.hasOwnProperty.call(inner, "totp") ||
      Object.prototype.hasOwnProperty.call(inner, "TOTP") ||
      Object.prototype.hasOwnProperty.call(inner, "email") ||
      Object.prototype.hasOwnProperty.call(inner, "mobile") ||
      Object.prototype.hasOwnProperty.call(inner, "passkey")
    ) {
      d = inner;
    }
  }
  const dPreFlatten = d;
  d = flattenOneLevelLogin2Step(d);
  // Top-level `totp` / `email` / `mobile` from the API must win over nested objects (e.g. `login2Step: { totp: true }` spread).
  const reapplyTop = [
    ["totp", "totp"],
    ["TOTP", "totp"],
    ["email", "email"],
    ["mobile", "mobile"],
    ["sms", "mobile"],
    ["phone", "mobile"],
    ["passkey", "passkey"],
  ];
  for (const [from, to] of reapplyTop) {
    if (dPreFlatten && typeof dPreFlatten === "object" && Object.prototype.hasOwnProperty.call(dPreFlatten, from)) {
      d[to] = dPreFlatten[from];
    }
  }
  if (!d || typeof d !== "object") return { totp: false, email: false, mobile: false, passkey: false };

  const fromArrayKeys = [
    "enabledMethods",
    "methods",
    "activeMethods",
    "loginMethods",
    "twoFactorMethods",
    "two_step_methods",
    "enabled_2fa",
  ];
  let fromArr = null;
  for (const k of fromArrayKeys) {
    if (d[k] != null) {
      fromArr = parseEnabledMethodsArray(d[k]);
      if (fromArr) break;
    }
  }

  const fromKeys = {
    totp: firstTruthyKey(d, [
      "totp",
      "TOTP",
      "google_authenticator",
      "googleAuthenticator",
      "isTotp",
      "totp_enabled",
    ]),
    email: firstTruthyKey(d, ["email", "emailVerification", "isEmail", "email_enabled", "is_email"]),
    mobile: firstTruthyKey(d, [
      "mobile",
      "sms",
      "phone",
      "smsVerification",
      "isPhone",
      "phone_verification",
      "is_phone",
    ]),
    passkey: firstTruthyKey(d, ["passkey", "webauthn", "isPasskey", "pass_key", "hasPasskey"]),
  };
  if (!fromArr) {
    return {
      totp: !!fromKeys.totp,
      email: !!fromKeys.email,
      mobile: !!fromKeys.mobile,
      passkey: !!fromKeys.passkey,
    };
  }
  // If the API already sent flat booleans, do not let `enabledMethods` arrays override them.
  const explicit = {
    totp: Object.prototype.hasOwnProperty.call(dPreFlatten, "totp") || Object.prototype.hasOwnProperty.call(dPreFlatten, "TOTP"),
    email: Object.prototype.hasOwnProperty.call(dPreFlatten, "email"),
    mobile: ["mobile", "sms", "phone"].some((k) => Object.prototype.hasOwnProperty.call(dPreFlatten, k)),
    passkey: Object.prototype.hasOwnProperty.call(dPreFlatten, "passkey"),
  };
  return {
    totp: explicit.totp ? !!fromKeys.totp : !!fromKeys.totp || fromArr.totp,
    email: explicit.email ? !!fromKeys.email : !!fromKeys.email || fromArr.email,
    mobile: explicit.mobile ? !!fromKeys.mobile : !!fromKeys.mobile || fromArr.mobile,
    passkey: explicit.passkey ? !!fromKeys.passkey : !!fromKeys.passkey || fromArr.passkey,
  };
};

/**
 * Security Settings — static markup. BEM classes: tf-sec-page, tf-sec-page__* (see security.css).
 * No <header> elements. Route: /user_profile/security
 */
const TwofactorPage = ({ userDetails = {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const go = (path) => navigate(path);

  const maskedEmail = useMemo(
    () => maskEmailForDisplay(userDetails?.emailId || userDetails?.email),
    [userDetails?.emailId, userDetails?.email]
  );
  const lastLoginIpMasked = useMemo(
    () => maskIpForDisplay(userDetails?.last_login_ip),
    [userDetails?.last_login_ip]
  );
  const [isLogin2StepOpen, setIsLogin2StepOpen] = useState(false);
  const [login2Step, setLogin2Step] = useState({
    googleAuthenticator: false,
    smsVerification: false,
    emailVerification: false,
    passkey: false,
  });
  const [googleVerifyOpen, setGoogleVerifyOpen] = useState(false);
  const [googleOtp, setGoogleOtp] = useState(["", "", "", "", "", ""]);
  const [googleToggleAction, setGoogleToggleAction] = useState("enable");
  const [googleVerifyTarget, setGoogleVerifyTarget] = useState("login"); // login | wds
  const [smsVerifyOpen, setSmsVerifyOpen] = useState(false);
  const [smsOtp, setSmsOtp] = useState(["", "", "", "", "", ""]);
  const [smsToggleAction, setSmsToggleAction] = useState("enable");
  const [smsVerifyTarget, setSmsVerifyTarget] = useState("login"); // login | wds
  const [bindPrompt, setBindPrompt] = useState({
    open: false,
    title: "Phone Unbound",
    description: "Phone verification is available only after the phone is bound.",
    route: "/user_profile/security/smsVerification",
  });
  const [emailVerifyOpen, setEmailVerifyOpen] = useState(false);
  const [emailOtp, setEmailOtp] = useState(["", "", "", "", "", ""]);
  const [emailResendLeft, setEmailResendLeft] = useState(53);
  const [emailVerifyTarget, setEmailVerifyTarget] = useState("login"); // login | wds
  const [emailToggleAction, setEmailToggleAction] = useState("enable");
  const [agceAuthBindOpen, setAgceAuthBindOpen] = useState(false);
  const [fundPasswordMissingOpen, setFundPasswordMissingOpen] = useState(false);
  /** Pre-navigation warning before opening fund password flow (24h withdrawal / P2P lock). */
  const [fundPasswordChangeConfirmOpen, setFundPasswordChangeConfirmOpen] = useState(false);
  const [trustedAddrMissingOpen, setTrustedAddrMissingOpen] = useState(false);
  const [isWithdrawSettingsOpen, setIsWithdrawSettingsOpen] = useState(false);
  const [withdrawSettings, setWithdrawSettings] = useState({
    smsVerification: false,
    fundPassword: false,
    emailVerification: true,
    trustedAddresses: false,
    googleAuthenticator: false,
    passkeyEnabled: false,
  });
  const [withdrawSettingsLoaded, setWithdrawSettingsLoaded] = useState(false);
  /** Linked OAuth providers count — Advanced Security → Account Connections row (GET get-connected-account-count). */
  const [connectedAccountCount, setConnectedAccountCount] = useState(0);
  /** Emergency contacts count — Advanced Security → Emergency Contact row (GET get-emergency-contact-count). */
  const [emergencyContactCount, setEmergencyContactCount] = useState(0);
  const [withdrawAddressMode, setWithdrawAddressMode] = useState("addressBook"); // addressBook | verificationFree
  // Withdrawal-settings toggle — pending action shared across modals
  const [wdsPendingToggle, setWdsPendingToggle] = useState(null); // { method, enable } | null
  // Fund-password verification modal for withdrawal settings toggle
  const [wdsFundPwOpen, setWdsFundPwOpen] = useState(false);
  const [wdsFundPwInput, setWdsFundPwInput] = useState("");
  const [wdsFundPwError, setWdsFundPwError] = useState("");

  // ============================================================================
  // Security-API driven state (integrated from SecuritySystem flow)
  // ============================================================================
  const profileCtx = useContext(ProfileContext) || {};
  const handleUserDetails = profileCtx.handleUserDetails || (() => { });
  const profileHydrated = profileCtx.profileHydrated ?? false;

  const [mobileNumber, setMobileNumber] = useState("");
  const [emailID, setEmailId] = useState("");

  const [hasEmail, setHasEmail] = useState(false);
  const [hasMobile, setHasMobile] = useState(false);
  const [hasGoogleAuth, setHasGoogleAuth] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [login2StepMethods, setLogin2StepMethods] = useState({
    email: false,
    mobile: false,
    totp: false,
  });

  /** Row + icons: merge `login2Step` with `login2StepMethods` (same API can populate either path) */
  const login2StepRowFlags = useMemo(
    () => ({
      passkey: !!login2Step.passkey,
      googleAuthenticator: !!(login2Step.googleAuthenticator || login2StepMethods.totp),
      smsVerification: !!(login2Step.smsVerification || login2StepMethods.mobile),
      emailVerification: !!(login2Step.emailVerification || login2StepMethods.email),
    }),
    [login2Step, login2StepMethods]
  );
  const login2StepRowAnyOn = useMemo(
    () =>
      login2StepRowFlags.googleAuthenticator ||
      login2StepRowFlags.smsVerification ||
      login2StepRowFlags.emailVerification,
    [login2StepRowFlags]
  );

  const [passkeys, setPasskeys] = useState([]);
  const [passkeyName, setPasskeyName] = useState("");
  const [selectedPasskey, setSelectedPasskey] = useState(null);
  const [deletePasskeyMethod, setDeletePasskeyMethod] = useState("totp");
  const [deletePasskeyCode, setDeletePasskeyCode] = useState("");

  // Disable Google Authenticator flow (inline overlay popup)
  // Supports email / mobile / passkey when the authenticator app is lost.
  const [isDisableGAOpen, setIsDisableGAOpen] = useState(false);
  const [disableGAOtp, setDisableGAOtp] = useState(["", "", "", "", "", ""]);
  const [isDisablingGA, setIsDisablingGA] = useState(false);
  const [disableGAMethod, setDisableGAMethod] = useState("totp");
  const [disableGAMethodsPickerOpen, setDisableGAMethodsPickerOpen] = useState(false);
  const [gaRemovedNotice, setGaRemovedNotice] = useState({ open: false, hours: 24 });

  const handleDisableGAOtpChange = (idx, e) => {
    const digit = String(e.target.value ?? "").replace(/\D/g, "").slice(-1);
    setDisableGAOtp((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < 5) {
      window.requestAnimationFrame(() => {
        document.getElementById(`ga-disable-otp-${idx + 1}`)?.focus();
      });
    }
  };

  const handleDisableGAOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !disableGAOtp[idx] && idx > 0) {
      window.requestAnimationFrame(() => {
        document.getElementById(`ga-disable-otp-${idx - 1}`)?.focus();
      });
    }
  };

  const pasteDisableGAOtp = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const digits = String(raw || "").replace(/\D/g, "").slice(0, 6).split("");
      if (digits.length !== 6) return;
      setDisableGAOtp(digits);
      window.requestAnimationFrame(() => {
        document.getElementById("ga-disable-otp-5")?.focus();
      });
    } catch {
      // ignore
    }
  };

  const disableGAOtpComplete = disableGAOtp.every((d) => d && d.length === 1);
  const [deletePasskeyAvailableMethods, setDeletePasskeyAvailableMethods] = useState([]);
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [isPasskeyVerifying, setIsPasskeyVerifying] = useState(false);

  const [googleQr, setGoogleQr] = useState("");
  const [googleCode, setGoogleCode] = useState("");
  const [authenticatorCode, setAuthenticatorCode] = useState("");

  const [emailOtpCode, setEmailOtpCode] = useState("");
  const [mobileOtpCode, setMobileOtpCode] = useState("");
  const [newEmailOtpCode, setNewEmailOtpCode] = useState("");
  const [newMobileOtpCode, setNewMobileOtpCode] = useState("");
  const [googleAuthCode, setGoogleAuthCode] = useState("");

  const [newEmail, setNewEmail] = useState("");
  const [newMobileNumber, setNewMobileNumber] = useState("");
  const [newCountryCode, setNewCountryCode] = useState("+91");

  const [addEmailStep, setAddEmailStep] = useState(1);

  const [disableAuthMethod, setDisableAuthMethod] = useState(2);
  const [disableAvailableMethods, setDisableAvailableMethods] = useState([]);

  const [changeVerifyMethod, setChangeVerifyMethod] = useState("email");
  const [changeAvailableMethods, setChangeAvailableMethods] = useState([]);

  const [addMobileVerifyMethod, setAddMobileVerifyMethod] = useState("email");
  const [addMobileAvailableMethods, setAddMobileAvailableMethods] = useState([]);
  const [passkeyVerificationResult, setPasskeyVerificationResult] = useState(null);

  const [resendTimer, setResendTimer] = useState(0);
  const [resendTimer2, setResendTimer2] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);

  const [fundPasswordStatus, setFundPasswordStatus] = useState(null);

  const fetchFundPasswordStatus = useCallback(async () => {
    try {
      const result = await AuthService.securityGetFundPasswordStatus();
      if (!result?.success) return;
      setFundPasswordStatus(!!result?.data);
    } catch {
      // fall back to profile-derived hasFundPassword
    }
  }, []);

  const [hasAntiPhishingCode, setHasAntiPhishingCode] = useState(false);
  const [securityOverviewReady, setSecurityOverviewReady] = useState(false);

  const [antiPhishingRemoveOpen, setAntiPhishingRemoveOpen] = useState(false);

  const fetchAntiPhishingStatus = useCallback(async () => {
    try {
      const result = await AuthService.antiPhishingGetStatus();
      if (!result?.success) return;
      const data = result?.data || {};
      setHasAntiPhishingCode(!!data?.hasAntiPhishingCode);
    } catch {
      // hub row relies on stale state until retry
    }
  }, []);

  const closeAntiPhishingRemove = useCallback(() => {
    setAntiPhishingRemoveOpen(false);
  }, []);

  const openAntiPhishingRemove = useCallback(() => {
    setAntiPhishingRemoveOpen(true);
  }, []);

  const syncLogin2StepFromApi = useCallback(async () => {
    try {
      const result = await AuthService.securityCheckTwoLogin2Step();
      if (!result || typeof result !== "object") return;
      // Prefer `data`; if backend only puts fields on the root, parse that (ignore success wrapper keys).
      let raw = result.data != null ? result.data : result;
      if (raw == null) return;
      if (typeof raw === "string") {
        try {
          raw = JSON.parse(raw);
        } catch {
          return;
        }
      }
      if (typeof raw !== "object" || Array.isArray(raw)) return;
      const n = normalizeCheckTwoLogin2StepPayload(raw);
      setLogin2Step({
        googleAuthenticator: n.totp,
        smsVerification: n.mobile,
        emailVerification: n.email,
        passkey: n.passkey,
      });
      setLogin2StepMethods({ email: n.email, mobile: n.mobile, totp: n.totp });
    } catch {
      // keep existing UI fallback on API failure
    }
  }, []);

  const updateTwoLogin2StepStatus = useCallback(async (securityMethod, code, action) => {
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securityUpdateTwoLogin2Step(securityMethod, code, action);
      if (!result?.success) {
        if (!isTotpNotConfiguredMessage(result?.message)) {
          alertErrorMessage(result?.message || "Failed to update 2-step status");
        }
        return result || { success: false, message: "Failed to update 2-step status" };
      }
      await syncLogin2StepFromApi();
      alertSuccessMessage(result?.message || "2-step status updated");
      return result;
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
      return { success: false, message: error?.message || "Something went wrong" };
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [syncLogin2StepFromApi]);

  const sendOtpForTwoLogin2Step = useCallback(async (securityMethod) => {
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securitySendOtpForTwoLogin2Step(securityMethod);
      if (!result?.success) {
        alertErrorMessage(result?.message || "Failed to send OTP");
        return false;
      }
      alertSuccessMessage(result?.message || "OTP sent successfully");
      return true;
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
      return false;
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, []);

  // eslint-disable-next-line no-unused-vars
  const [showSecurityReminder, setShowSecurityReminder] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [pendingAction, setPendingAction] = useState(null);

  const [addPasskeyVerifyMethod, setAddPasskeyVerifyMethod] = useState("email");
  const [addPasskeyAvailableMethods, setAddPasskeyAvailableMethods] = useState([]);
  const [addPasskeyOtpCode, setAddPasskeyOtpCode] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [addPasskeyStep, setAddPasskeyStep] = useState(1);

  // ============ Modal helpers (Bootstrap based) ============
  const openModal = (modalId) => {
    const el = document.getElementById(modalId);
    if (!el || !window.bootstrap) return;
    const modal = window.bootstrap.Modal.getOrCreateInstance(el);
    modal.show();
  };

  const closeModal = (modalId) => {
    const el = document.getElementById(modalId);
    if (!el || !window.bootstrap) return;
    const modal = window.bootstrap.Modal.getInstance(el);
    if (modal) modal.hide();
  };

  const switchModal = (closeId, openId, delay = 300) => {
    closeModal(closeId);
    setTimeout(() => openModal(openId), delay);
  };

  const resetModalStates = () => {
    setEmailOtpCode("");
    setMobileOtpCode("");
    setNewEmailOtpCode("");
    setNewMobileOtpCode("");
    setGoogleAuthCode("");
    setNewEmail("");
    setNewMobileNumber("");
    setNewCountryCode("+91");
    setAuthenticatorCode("");
    setGoogleQr("");
    setGoogleCode("");
    setCurrentStep(1);
    setResendTimer(0);
    setResendTimer2(0);
    setPasskeyName("");
    setSelectedPasskey(null);
    setDeletePasskeyCode("");
  };

  const closeAllModals = () => {
    const modalIds = [
      "googleAuthSetupModal", "googleAuthQrModal", "googleAuthVerifyModal",
      "addMobileModal", "addEmailModal",
      "changeEmailModal", "changeMobileModal",
      "blockingModal", "disableGoogleAuthModal", "disableAuthOptionsModal",
      "securityReminderModal", "addPasskeyModal", "addPasskeyVerifyModal", "addPasskeyVerifyOptionsModal",
      "deletePasskeyModal", "deletePasskeyOptionsModal",
      "changeVerifyOptionsModal", "changeMobileVerifyOptionsModal", "addMobileVerifyOptionsModal",
      "changeEmailConsentModal", "changeMobileConsentModal", "disablePasskeyConsentModal", "disableGoogleAuthConsentModal",
      "viewPasskeysModal",
    ];
    modalIds.forEach((id) => closeModal(id));
    document.body.classList.remove("modal-open");
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    resetModalStates();
  };

  // ============ Data fetchers ============
  const checkPasskeySupport = useCallback(async () => {
    if (window.PublicKeyCredential === undefined || typeof window.PublicKeyCredential !== "function") {
      setPasskeySupported(false);
      return false;
    }
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    try {
      const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (!available && isIOS && isSafari) {
        const match = navigator.userAgent.match(/OS (\d+)_/);
        const iosVersion = match ? parseInt(match[1], 10) : 0;
        if (iosVersion >= 16) {
          setPasskeySupported(true);
          return true;
        }
      }
      setPasskeySupported(available);
      return available;
    } catch {
      if (isIOS && isSafari) {
        setPasskeySupported(true);
        return true;
      }
      setPasskeySupported(false);
      return false;
    }
  }, []);

  const fetchPasskeys = useCallback(async () => {
    try {
      const result = await AuthService.passkeyGetList();
      if (result?.success && result?.data) {
        setPasskeys(result.data.passkeys || []);
        setHasPasskey((result.data.count || 0) > 0);
      }
    } catch {
      // silent
    }
  }, []);

  const fetchSecurityMethodsList = useCallback(async () => {
    try {
      const result = await AuthService.getSecurityMethodsList();
      const methods =
        result?.data?.security_methods ||
        result?.data?.data?.security_methods ||
        result?.security_methods ||
        result?.data?.securityMethods ||
        null;
      if (result?.success && methods && typeof methods === "object") {
        setHasEmail(!!methods.email);
        setHasMobile(!!methods.mobile);
        setHasGoogleAuth(!!methods.totp);
        setHasPasskey(!!methods.passkey);
      }
      return result?.success ? methods : null;
    } catch {
      // silent
      return null;
    }
  }, []);

  const applyGoogleAuthRemoved = useCallback(
    async (result, { showNotice = true } = {}) => {
      setHasGoogleAuth(false);
      setLogin2Step((s) => ({ ...s, googleAuthenticator: false }));
      setLogin2StepMethods((s) => ({ ...s, totp: false }));
      if (showNotice) {
        setGaRemovedNotice({
          open: true,
          hours: restrictionHoursFromDisableResult(result),
        });
      }
      try {
        handleUserDetails();
      } catch {
        // profile refresh is best-effort
      }
      await Promise.all([syncLogin2StepFromApi(), fetchSecurityMethodsList()]);
    },
    [handleUserDetails, syncLogin2StepFromApi, fetchSecurityMethodsList]
  );

  const handlePasswordChangeClick = useCallback(async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const methods = await fetchSecurityMethodsList();
      if (!methods) {
        alertErrorMessage("Failed to fetch security methods.");
        return;
      }
      const preferred =
        methods?.passkey
          ? "passkey"
          : (methods?.totp ? "totp" : (methods?.email ? "email" : (methods?.mobile ? "mobile" : null)));
      if (!preferred) {
        alertErrorMessage("No supported verification method found. Please enable Email, Mobile, or Google Authenticator.");
        return;
      }
      navigate("/user_profile/security/changeLoginPassword", {
        state: {
          passwordVerifyMethod: preferred,
          availableSecurityMethods: methods,
        },
      });
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, [fetchSecurityMethodsList, navigate]);

  const handleDismissSecurityPrompt = () => {
    setShowSecurityReminder(false);
    closeModal("securityReminderModal");
  };

  // ============ Init user data ============
  useEffect(() => {
    if (!profileHydrated) return undefined;

    let alive = true;

    const initSecurityData = async () => {
      const current2fa = userDetails?.["2fa"] || 0;
      setEmailId(userDetails?.emailId || "");
      setMobileNumber(`${userDetails?.country_code || ""} ${userDetails?.mobileNumber || ""}`);
      setHasEmail(!!userDetails?.emailId);
      setHasMobile(!!userDetails?.mobileNumber);
      setHasGoogleAuth(current2fa === 2);
      checkPasskeySupport();

      await Promise.all([fetchPasskeys(), syncLogin2StepFromApi(), fetchAntiPhishingStatus(), fetchSecurityMethodsList()]);

      if (alive) {
        setSecurityOverviewReady(true);
      }
    };

    initSecurityData();

    return () => {
      alive = false;
    };
  }, [profileHydrated, userDetails, checkPasskeySupport, fetchPasskeys, syncLogin2StepFromApi, fetchAntiPhishingStatus, fetchSecurityMethodsList]);

  useEffect(() => {
    if (!isLogin2StepOpen) return;
    syncLogin2StepFromApi();
  }, [isLogin2StepOpen, syncLogin2StepFromApi]);

  useEffect(() => {
    fetchFundPasswordStatus();
  }, [fetchFundPasswordStatus]);

  useEffect(() => {
    if (isDisableGAOpen && (disableGAMethod === "email" || disableGAMethod === "mobile")) {
      const autoSend = async () => {
        if (resendTimer <= 0) {
          const sent = await handleSendOtp(disableGAMethod, "2fa_disable");
          if (sent) setResendTimer(60);
        }
      };
      void autoSend();
    }
  }, [isDisableGAOpen, disableGAMethod]);

  // ============ Resend timers ============
  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  useEffect(() => {
    if (resendTimer2 <= 0) return undefined;
    const t = setTimeout(() => setResendTimer2((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer2]);

  // ============ Security level ============
  const getActiveMethodsCount = () => {
    let count = 0;
    if (hasEmail) count += 1;
    if (hasMobile) count += 1;
    if (hasGoogleAuth) count += 1;
    if (hasPasskey) count += 1;
    return count;
  };

  const canMakeSensitiveChanges = () => getActiveMethodsCount() >= 2;

  const securityLevelItems = useMemo(
    () => [
      { id: "passkey", label: "Passkey", enabled: !!hasPasskey, enabledLabel: "Active", disabledLabel: "Setup needed" },
      { id: "google", label: "Google Authenticator", enabled: !!hasGoogleAuth, enabledLabel: "Active", disabledLabel: "Setup needed" },
      { id: "email", label: "Email verification", enabled: !!hasEmail, enabledLabel: "Bound", disabledLabel: "Add email" },
      { id: "sms", label: "SMS verification", enabled: !!hasMobile, enabledLabel: "Bound", disabledLabel: "Add phone" },
      { id: "antiPhishing", label: "Anti-Phishing Code", enabled: !!hasAntiPhishingCode, enabledLabel: "Active", disabledLabel: "Setup needed" },
      {
        id: "login2step",
        label: "Login 2-Step Verification",
        enabled: !!login2StepRowAnyOn,
        enabledLabel: "On",
        disabledLabel: "Off",
      },
    ],
    [hasPasskey, hasGoogleAuth, hasEmail, hasMobile, hasAntiPhishingCode, login2StepRowAnyOn]
  );

  // ============ Generic OTP helpers ============
  const handleSendOtp = async (target, purpose, value = null) => {
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securitySendOtp(target, purpose, value);
      if (result?.success) {
        alertSuccessMessage(result?.message || "OTP sent successfully");
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send OTP");
      return false;
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
      return false;
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleVerifyOtp = async (target, otp, purpose, identifier = null) => {
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securityVerifyOtp(target, otp, purpose, identifier);
      if (result?.success) return true;
      alertErrorMessage(result?.message || "Invalid OTP");
      return false;
    } catch (error) {
      alertErrorMessage(error?.message || "OTP verification failed");
      return false;
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const sendOtpForLogin2Step = async (securityMethods) => {
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.sendOtpForTwoLogin2Step(securityMethods);
      if (result?.success) {
        alertSuccessMessage(result?.message || "OTP sent successfully");
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send OTP");
      return false;
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
      return false;
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  // ============ Passkey verification (explicit + silent) ============
  const handlePasskeyVerification = async () => {
    if (!passkeySupported) {
      alertErrorMessage("Passkeys are not supported on this device/browser");
      return null;
    }
    try {
      setIsPasskeyVerifying(true);
      LoaderHelper.loaderStatus(true);
      const optionsResult = await AuthService.passkeyGetStepUpOptions();
      if (!optionsResult?.success || !optionsResult?.data) {
        alertErrorMessage(optionsResult?.message || "Failed to get passkey options");
        return null;
      }
      let credential;
      try {
        credential = await startAuthentication(optionsResult.data);
      } catch (webauthnError) {
        if (webauthnError?.name === "NotAllowedError") {
          alertErrorMessage("Authentication was cancelled or timed out. Please try again.");
        } else {
          alertErrorMessage("Failed to authenticate with passkey. Please try another method.");
        }
        return null;
      }
      const verifyResult = await AuthService.passkeyVerifyStepUp(credential);
      if (verifyResult?.success) {
        alertSuccessMessage("Passkey verified successfully");
        return verifyResult.data;
      }
      alertErrorMessage(verifyResult?.message || "Passkey verification failed");
      return null;
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
      return null;
    } finally {
      setIsPasskeyVerifying(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const attemptSilentPasskeyVerification = async () => {
    if (!hasPasskey || !passkeySupported) return null;
    try {
      setIsPasskeyVerifying(true);
      LoaderHelper.loaderStatus(true);
      const optionsResult = await AuthService.passkeyGetStepUpOptions();
      if (!optionsResult?.success || !optionsResult?.data) return null;
      let credential;
      try {
        credential = await startAuthentication(optionsResult.data);
      } catch {
        return null;
      }
      const verifyResult = await AuthService.passkeyVerifyStepUp(credential);
      if (verifyResult?.success) {
        alertSuccessMessage("Passkey verified successfully");
        return verifyResult.data;
      }
      return null;
    } catch {
      return null;
    } finally {
      setIsPasskeyVerifying(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleLogin2StepPasskeyToggle = async () => {
    if (!passkeySupported) {
      alertErrorMessage("Passkeys are not supported on this device/browser");
      return;
    }
    const action = login2Step.passkey ? "disable" : "enable";
    if (action === "enable" && !hasPasskey) {
      setIsLogin2StepOpen(false);
      window.setTimeout(() => go("/user_profile/security/passkey"), 0);
      return;
    }
    const vr = await handlePasskeyVerification();
    const passkeyUserId = vr?.userId ?? vr?.passkeyUserId ?? vr?.id;
    if (!passkeyUserId) return;
    const result = await updateTwoLogin2StepStatus("passkey", String(passkeyUserId), action);
    if (!result?.success) return;
    setLogin2Step((s) => ({ ...s, passkey: action === "enable" }));
  };

  // ============ Disable Google Authenticator (multi-method recovery) ============
  const disableGAAvailableMethods = useMemo(() => {
    const methods = [];
    if (hasPasskey && passkeySupported) {
      methods.push({
        id: "passkey",
        label: "Passkey",
        description: "Use Face ID, Touch ID, or Windows Hello",
      });
    }
    if (hasEmail) {
      methods.push({
        id: "email",
        label: "Email",
        description: `Send code to ${maskEmail(emailID)}`,
      });
    }
    if (hasMobile) {
      methods.push({
        id: "mobile",
        label: "Mobile",
        description: `Send code to ${maskPhone(mobileNumber)}`,
      });
    }
    methods.push({
      id: "totp",
      label: "Google Authenticator",
      description: "Use your authenticator app",
    });
    return methods;
  }, [hasPasskey, passkeySupported, hasEmail, hasMobile, emailID, mobileNumber]);

  const pickDefaultDisableGAMethod = useCallback(() => {
    // Prefer recovery channels over TOTP so lost-authenticator users are not stuck
    if (hasPasskey && passkeySupported) return "passkey";
    if (hasEmail) return "email";
    if (hasMobile) return "mobile";
    return "totp";
  }, [hasPasskey, passkeySupported, hasEmail, hasMobile]);

  const openDisableGA = () => {
    setDisableGAOtp(["", "", "", "", "", ""]);
    setResendTimer(0);
    setDisableGAMethodsPickerOpen(false);
    const method = pickDefaultDisableGAMethod();
    setDisableGAMethod(method);
    setIsDisableGAOpen(true);
    if (method !== "passkey") {
      window.requestAnimationFrame(() => {
        document.getElementById("ga-disable-otp-0")?.focus();
      });
    }
  };

  const closeDisableGA = () => {
    if (isDisablingGA) return;
    setIsDisableGAOpen(false);
    setDisableGAOtp(["", "", "", "", "", ""]);
    setDisableGAMethodsPickerOpen(false);
    setResendTimer(0);
  };

  const selectDisableGAMethod = (methodId) => {
    setDisableGAMethod(methodId);
    setDisableGAOtp(["", "", "", "", "", ""]);
    setResendTimer(0);
    setDisableGAMethodsPickerOpen(false);
    if (methodId !== "passkey") {
      window.requestAnimationFrame(() => {
        document.getElementById("ga-disable-otp-0")?.focus();
      });
    }
  };

  const sendDisableGAOtp = async () => {
    if (disableGAMethod !== "email" && disableGAMethod !== "mobile") return;
    if (resendTimer > 0) return;
    const sent = await handleSendOtp(disableGAMethod, "2fa_disable");
    if (sent) setResendTimer(60);
  };

  const getDisableGASubtitle = () => {
    if (disableGAMethod === "passkey") return "Verify with your passkey to disable Google Authenticator. Login 2-Step will stop using it, and withdrawals are paused for 24 hours.";
    if (disableGAMethod === "email") return `We'll send a verification code to ${maskEmail(emailID)}. Login 2-Step will stop using authenticator, and withdrawals are paused for 24 hours.`;
    if (disableGAMethod === "mobile") return `We'll send a verification code to ${maskPhone(mobileNumber)}. Login 2-Step will stop using authenticator, and withdrawals are paused for 24 hours.`;
    return "Enter the 6-digit code from your Google Authenticator app to disable it. Login 2-Step will stop using it, and withdrawals are paused for 24 hours.";
  };

  const handleConfirmDisableGA = async () => {
    if (isDisablingGA) return;

    try {
      setIsDisablingGA(true);
      LoaderHelper.loaderStatus(true);
      let result;

      if (disableGAMethod === "passkey") {
        const vr = await handlePasskeyVerification();
        const passkeyUserId = vr?.userId ?? vr?.passkeyUserId ?? vr?.id;
        if (!passkeyUserId) return;
        result = await AuthService.security2faDisable(null, null, "passkey", passkeyUserId);
      } else if (disableGAMethod === "totp") {
        if (!disableGAOtpComplete) {
          alertErrorMessage("Please enter a valid 6-digit code");
          return;
        }
        result = await AuthService.security2faDisable(disableGAOtp.join(""));
      } else {
        if (!disableGAOtpComplete) {
          alertErrorMessage("Please enter a valid 6-digit code");
          return;
        }
        result = await AuthService.security2faDisable(
          null,
          disableGAOtp.join(""),
          disableGAMethod
        );
      }

      if (result?.success) {
        alertSuccessMessage(result?.message || "Google Authenticator disabled");
        setIsDisableGAOpen(false);
        setDisableGAOtp(["", "", "", "", "", ""]);
        setDisableGAMethodsPickerOpen(false);
        await applyGoogleAuthRemoved(result);
      } else {
        alertErrorMessage(result?.message || "Failed to disable Google Authenticator");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsDisablingGA(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  // ============ Google Authenticator flow ============
  const handleGoogleAuthStart = async () => {
    resetModalStates();
    setPasskeyVerificationResult(null);

    if (hasPasskey && passkeySupported) {
      const vr = await attemptSilentPasskeyVerification();
      if (vr) {
        setPasskeyVerificationResult(vr);
        try {
          setIsLoading(true);
          LoaderHelper.loaderStatus(true);
          const result = await AuthService.security2faSetup();
          if (result?.success) {
            setGoogleQr(result.data?.qr_code || "");
            setGoogleCode(result.data?.secret?.base32 || "");
            openModal("googleAuthQrModal");
          } else {
            alertErrorMessage(result?.message || "Failed to get QR code");
          }
        } catch (error) {
          alertErrorMessage(error?.message || "Something went wrong");
        } finally {
          setIsLoading(false);
          LoaderHelper.loaderStatus(false);
        }
        return;
      }
    }

    if (hasEmail || hasMobile) {
      openModal("googleAuthSetupModal");
    } else {
      alertErrorMessage("You need an email or mobile number to set up Google Authenticator");
    }
  };

  const handleGoogleAuthVerifyAndSetup = async () => {
    const otpCode = hasEmail ? emailOtpCode : mobileOtpCode;
    const target = hasEmail ? "email" : "mobile";
    if (!otpCode || otpCode.length !== 6) {
      alertErrorMessage("Please enter a valid 6-digit OTP");
      return;
    }
    const verified = await handleVerifyOtp(target, otpCode, "2fa_setup");
    if (!verified) return;
    alertSuccessMessage("Verified! Generating QR code...");
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.security2faSetup();
      if (result?.success) {
        setGoogleQr(result.data?.qr_code || "");
        setGoogleCode(result.data?.secret?.base32 || "");
        switchModal("googleAuthSetupModal", "googleAuthQrModal");
      } else {
        alertErrorMessage(result?.message || "Failed to get QR code");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleGoogleAuthConfirm = async () => {
    if (!authenticatorCode || authenticatorCode.length !== 6) {
      alertErrorMessage("Please enter a valid 6-digit code");
      return;
    }
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.security2faConfirm(authenticatorCode);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Google Authenticator enabled successfully!");
        closeAllModals();
        handleUserDetails();
      } else {
        alertErrorMessage(result?.message || "Failed to enable 2FA");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleDisableGoogleAuthStart = () => {
    if (!canMakeSensitiveChanges()) {
      openModal("blockingModal");
      return;
    }
    setPendingAction("disableGoogleAuth");
    openModal("disableGoogleAuthConsentModal");
  };

  const handleDisableGoogleAuthProceed = async () => {
    closeModal("disableGoogleAuthConsentModal");
    resetModalStates();
    setPasskeyVerificationResult(null);

    if (hasPasskey && passkeySupported) {
      const vr = await attemptSilentPasskeyVerification();
      if (vr) {
        try {
          setIsLoading(true);
          LoaderHelper.loaderStatus(true);
          const result = await AuthService.security2faDisable(null, null, "passkey", vr.userId);
          if (result?.success) {
            alertSuccessMessage(result?.message || "Google Authenticator disabled successfully");
            await applyGoogleAuthRemoved(result);
          } else {
            alertErrorMessage(result?.message || "Failed to disable Google Authenticator");
          }
        } catch (error) {
          alertErrorMessage(error?.message || "Something went wrong");
        } finally {
          setIsLoading(false);
          LoaderHelper.loaderStatus(false);
        }
        return;
      }
    }

    const methods = [];
    if (hasPasskey && passkeySupported) {
      methods.push({ type: 4, label: "Passkey", value: "Passkey", icon: "ri-fingerprint-line", description: "Use Face ID, Touch ID, or Windows Hello" });
    }
    methods.push({ type: 2, label: "Google Authenticator", value: "Google Authenticator", icon: "ri-shield-keyhole-line", description: "Use your Google Authenticator app" });
    if (hasEmail) methods.push({ type: 1, label: "Email", value: maskEmail(emailID), icon: "ri-mail-line", description: "Receive verification code via email" });
    if (hasMobile) methods.push({ type: 3, label: "Mobile", value: maskPhone(mobileNumber), icon: "ri-smartphone-line", description: "Receive verification code via SMS" });

    setDisableAvailableMethods(methods);
    setDisableAuthMethod(hasPasskey && passkeySupported ? 4 : 2);
    setResendTimer(0);
    openModal("disableGoogleAuthModal");
  };

  const handleOpenDisableOptionsPopup = () => {
    closeModal("disableGoogleAuthModal");
    setTimeout(() => openModal("disableAuthOptionsModal"), 100);
  };

  const handleSelectDisableMethod = (method) => {
    setDisableAuthMethod(method.type);
    setEmailOtpCode("");
    setMobileOtpCode("");
    setGoogleAuthCode("");
    setResendTimer(0);
    closeModal("disableAuthOptionsModal");
    setTimeout(() => openModal("disableGoogleAuthModal"), 100);
  };

  const handleCloseDisableOptionsPopup = () => {
    closeModal("disableAuthOptionsModal");
    setTimeout(() => openModal("disableGoogleAuthModal"), 100);
  };

  const getDisableVerificationTitle = () => {
    if (disableAuthMethod === 4) return "Passkey Verification";
    if (disableAuthMethod === 2) return "Enter Google Authenticator Code";
    if (disableAuthMethod === 1) return "Enter Email Verification Code";
    if (disableAuthMethod === 3) return "Enter Mobile Verification Code";
    return "Verify Your Identity";
  };

  const getDisableVerificationDescription = () => {
    if (disableAuthMethod === 4) return "Use your passkey to verify your identity";
    if (disableAuthMethod === 2) return "Enter the 6-digit code from your authenticator app";
    if (disableAuthMethod === 1) return `We'll send a verification code to ${maskEmail(emailID)}`;
    if (disableAuthMethod === 3) return `We'll send a verification code to ${maskPhone(mobileNumber)}`;
    return "";
  };

  const getDisableOtpCode = () => {
    if (disableAuthMethod === 4) return "";
    if (disableAuthMethod === 2) return googleAuthCode;
    if (disableAuthMethod === 1) return emailOtpCode;
    if (disableAuthMethod === 3) return mobileOtpCode;
    return "";
  };

  const setDisableOtpCode = (value) => {
    if (disableAuthMethod === 2) setGoogleAuthCode(value);
    else if (disableAuthMethod === 1) setEmailOtpCode(value);
    else if (disableAuthMethod === 3) setMobileOtpCode(value);
  };

  const sendDisableOtp = async () => {
    if (disableAuthMethod === 4) return true;
    if (disableAuthMethod === 2) return true;
    const target = disableAuthMethod === 1 ? "email" : "mobile";
    const sent = await handleSendOtp(target, "2fa_disable");
    if (sent) setResendTimer(60);
    return sent;
  };

  const handleDisableGoogleAuthVerify = async () => {
    if (disableAuthMethod === 4) {
      const vr = await handlePasskeyVerification();
      if (!vr) return;
      try {
        setIsLoading(true);
        LoaderHelper.loaderStatus(true);
        const result = await AuthService.security2faDisable(null, null, "passkey", vr.userId);
        if (result?.success) {
          alertSuccessMessage(result?.message || "Google Authenticator disabled successfully");
          closeAllModals();
          await applyGoogleAuthRemoved(result);
        } else {
          alertErrorMessage(result?.message || "Failed to disable Google Authenticator");
        }
      } catch (error) {
        alertErrorMessage(error?.message || "Something went wrong");
      } finally {
        setIsLoading(false);
        LoaderHelper.loaderStatus(false);
      }
      return;
    }

    const otpCode = getDisableOtpCode();
    if (!otpCode || otpCode.length !== 6) {
      alertErrorMessage("Please enter a valid 6-digit code");
      return;
    }
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      let result;
      if (disableAuthMethod === 2) {
        result = await AuthService.security2faDisable(otpCode);
      } else {
        const verifyMethod = disableAuthMethod === 1 ? "email" : "mobile";
        result = await AuthService.security2faDisable(null, otpCode, verifyMethod);
      }
      if (result?.success) {
        alertSuccessMessage(result?.message || "Google Authenticator disabled");
        closeAllModals();
        await applyGoogleAuthRemoved(result);
      } else {
        alertErrorMessage(result?.message || "Failed to disable 2FA");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const copyCode = () => {
    if (!googleCode) return;
    navigator.clipboard.writeText(googleCode)
      .then(() => alertSuccessMessage("Code copied to clipboard!"))
      .catch(() => alertErrorMessage("Failed to copy code"));
  };

  // ============ Passkey flow ============
  const handleAddPasskeyStart = async () => {
    if (!passkeySupported) {
      alertErrorMessage("Passkeys are not supported on this device/browser");
      return;
    }
    resetModalStates();
    setAddPasskeyStep(1);
    setAddPasskeyOtpCode("");
    setResendTimer(0);

    const projectName = process.env.REACT_APP_PROJECT_NAME || "Exchange";
    const maskedIdentifier = emailID ? maskEmail(emailID) : (mobileNumber ? maskPhone(mobileNumber) : "");
    setPasskeyName(maskedIdentifier ? `${projectName} - ${maskedIdentifier}` : `${projectName} Passkey`);

    if (hasPasskey) {
      const vr = await attemptSilentPasskeyVerification();
      if (vr) {
        setAddPasskeyStep(2);
        openModal("addPasskeyModal");
        return;
      }
    }

    const methods = [];
    if (hasEmail) methods.push({ id: "email", label: "Email OTP", icon: "ri-mail-line", description: `Send code to ${maskEmail(emailID)}` });
    if (hasMobile) methods.push({ id: "mobile", label: "Mobile OTP", icon: "ri-smartphone-line", description: `Send code to ${maskPhone(mobileNumber)}` });
    if (hasGoogleAuth) methods.push({ id: "totp", label: "Google Authenticator", icon: "ri-shield-keyhole-line", description: "Use your authenticator app" });

    setAddPasskeyAvailableMethods(methods);
    if (hasGoogleAuth) setAddPasskeyVerifyMethod("totp");
    else if (hasEmail) setAddPasskeyVerifyMethod("email");
    else if (hasMobile) setAddPasskeyVerifyMethod("mobile");

    openModal("addPasskeyVerifyModal");
  };

  const handleAddPasskeySendOtp = async () => {
    if (addPasskeyVerifyMethod === "totp") return;
    const target = addPasskeyVerifyMethod === "email" ? "email" : "mobile";
    try {
      setIsLoading(true);
      const result = await AuthService.securitySendOtp(target, "add_passkey");
      if (result?.success) {
        alertSuccessMessage(result?.message || `OTP sent to your ${target}`);
        setResendTimer(60);
      } else {
        alertErrorMessage(result?.message || "Failed to send OTP");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPasskeyVerify = async () => {
    if (addPasskeyVerifyMethod === "totp") {
      if (!addPasskeyOtpCode || addPasskeyOtpCode.length !== 6) {
        alertErrorMessage("Please enter a valid 6-digit code");
        return;
      }
      try {
        setIsLoading(true);
        LoaderHelper.loaderStatus(true);
        const result = await AuthService.securityVerifyTotp(String(addPasskeyOtpCode), "add_passkey");
        if (result?.success) {
          alertSuccessMessage("Verified!");
          setAddPasskeyStep(2);
          switchModal("addPasskeyVerifyModal", "addPasskeyModal");
        } else {
          alertErrorMessage(result?.message || "Invalid code");
        }
      } catch (error) {
        alertErrorMessage(error?.message || "Verification failed");
      } finally {
        setIsLoading(false);
        LoaderHelper.loaderStatus(false);
      }
    } else {
      if (!addPasskeyOtpCode || addPasskeyOtpCode.length !== 6) {
        alertErrorMessage("Please enter a valid 6-digit OTP");
        return;
      }
      const target = addPasskeyVerifyMethod === "email" ? "email" : "mobile";
      const verified = await handleVerifyOtp(target, addPasskeyOtpCode, "add_passkey");
      if (verified) {
        alertSuccessMessage("Verified!");
        setAddPasskeyStep(2);
        switchModal("addPasskeyVerifyModal", "addPasskeyModal");
      }
    }
  };

  const getAddPasskeyVerificationTitle = () => {
    switch (addPasskeyVerifyMethod) {
      case "email": return "Email Verification";
      case "mobile": return "Mobile Verification";
      case "totp": return "Google Authenticator";
      default: return "Verification";
    }
  };

  const getAddPasskeyVerificationDescription = () => {
    switch (addPasskeyVerifyMethod) {
      case "email": return `Enter the 6-digit code sent to ${maskEmail(emailID)}`;
      case "mobile": return `Enter the 6-digit code sent to ${maskPhone(mobileNumber)}`;
      case "totp": return "Enter the 6-digit code from your authenticator app";
      default: return "Enter your verification code";
    }
  };

  const handleRegisterPasskey = async () => {
    if (!passkeyName.trim()) {
      alertErrorMessage("Please enter a name for your passkey");
      return;
    }
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const optionsResult = await AuthService.passkeyGetRegistrationOptions();
      if (!optionsResult?.success || !optionsResult?.data) {
        alertErrorMessage(optionsResult?.message || "Failed to get registration options");
        return;
      }
      let credential;
      try {
        credential = await startRegistration(optionsResult.data);
      } catch (webauthnError) {
        if (webauthnError?.name === "NotAllowedError") {
          alertErrorMessage("Registration was cancelled or timed out. Please try again.");
        } else if (webauthnError?.name === "InvalidStateError") {
          alertErrorMessage("This passkey is already registered on this device.");
        } else {
          alertErrorMessage("Failed to create passkey. Please try again.");
        }
        return;
      }
      const verifyResult = await AuthService.passkeyVerifyRegistration(credential, passkeyName.trim());
      if (verifyResult?.success) {
        alertSuccessMessage(verifyResult?.message || "Passkey added successfully!");
        closeAllModals();
        fetchPasskeys();
        handleUserDetails();
      } else {
        alertErrorMessage(verifyResult?.message || "Failed to register passkey");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleDeletePasskeyStart = (passkey) => {
    setSelectedPasskey(passkey);
    setPendingAction("disablePasskey");
    openModal("disablePasskeyConsentModal");
  };

  const handleDeletePasskeyProceed = async () => {
    closeModal("disablePasskeyConsentModal");
    setDeletePasskeyCode("");
    setResendTimer(0);

    if (hasPasskey && passkeySupported) {
      const vr = await attemptSilentPasskeyVerification();
      if (vr) {
        try {
          setIsLoading(true);
          LoaderHelper.loaderStatus(true);
          const result = await AuthService.passkeyDelete(selectedPasskey?._id, "passkey", null, vr.userId);
          if (result?.success) {
            alertSuccessMessage(result?.message || "Passkey deleted successfully");
            fetchPasskeys();
            handleUserDetails();
          } else {
            alertErrorMessage(result?.message || "Failed to delete passkey");
          }
        } catch (error) {
          alertErrorMessage(error?.message || "Something went wrong");
        } finally {
          setIsLoading(false);
          LoaderHelper.loaderStatus(false);
        }
        return;
      }
    }

    const methods = [];
    if (hasPasskey && passkeySupported) methods.push({ value: "passkey", label: "Passkey", icon: "ri-fingerprint-line", description: "Use passkey to verify" });
    if (hasGoogleAuth) methods.push({ value: "totp", label: "Google Authenticator", icon: "ri-shield-keyhole-line", description: "Use your authenticator app" });
    if (hasEmail) methods.push({ value: "email", label: "Email Verification", icon: "ri-mail-line", description: `Send code to ${maskEmail(emailID)}` });
    if (hasMobile) methods.push({ value: "mobile", label: "Mobile Verification", icon: "ri-smartphone-line", description: `Send code to ${maskPhone(mobileNumber)}` });

    setDeletePasskeyAvailableMethods(methods);
    if (hasPasskey && passkeySupported) setDeletePasskeyMethod("passkey");
    else if (hasGoogleAuth) setDeletePasskeyMethod("totp");
    else if (hasEmail) setDeletePasskeyMethod("email");
    else if (hasMobile) setDeletePasskeyMethod("mobile");

    openModal("deletePasskeyModal");
  };

  const handleDeletePasskey = async () => {
    if (!selectedPasskey) {
      alertErrorMessage("No passkey selected");
      return;
    }
    if (deletePasskeyMethod === "passkey") {
      const vr = await handlePasskeyVerification();
      if (!vr) return;
      try {
        setIsLoading(true);
        LoaderHelper.loaderStatus(true);
        const result = await AuthService.passkeyDelete(selectedPasskey._id, "passkey", null, vr.userId);
        if (result?.success) {
          alertSuccessMessage(result?.message || "Passkey deleted successfully");
          closeAllModals();
          fetchPasskeys();
          handleUserDetails();
        } else {
          alertErrorMessage(result?.message || "Failed to delete passkey");
        }
      } catch (error) {
        alertErrorMessage(error?.message || "Something went wrong");
      } finally {
        setIsLoading(false);
        LoaderHelper.loaderStatus(false);
      }
      return;
    }
    if (!deletePasskeyCode || deletePasskeyCode.length !== 6) {
      alertErrorMessage("Please enter a valid 6-digit code");
      return;
    }
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.passkeyDelete(selectedPasskey._id, deletePasskeyMethod, deletePasskeyCode);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Passkey deleted successfully");
        closeAllModals();
        fetchPasskeys();
        handleUserDetails();
      } else {
        alertErrorMessage(result?.message || "Failed to delete passkey");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const getDeleteVerificationDescription = () => {
    switch (deletePasskeyMethod) {
      case "passkey": return "Use another passkey to verify your identity";
      case "totp": return "Enter the 6-digit code from your authenticator app";
      case "email": return `We'll send a verification code to ${maskEmail(emailID)}`;
      case "mobile": return `We'll send a verification code to ${maskPhone(mobileNumber)}`;
      default: return "";
    }
  };

  const handleOpenDeletePasskeyOptions = () => {
    closeModal("deletePasskeyModal");
    setTimeout(() => openModal("deletePasskeyOptionsModal"), 100);
  };

  const handleSelectDeletePasskeyMethod = (method) => {
    setDeletePasskeyMethod(method.value);
    setDeletePasskeyCode("");
    setResendTimer(0);
    closeModal("deletePasskeyOptionsModal");
    setTimeout(() => openModal("deletePasskeyModal"), 100);
  };

  const handleCloseDeletePasskeyOptionsPopup = () => {
    closeModal("deletePasskeyOptionsModal");
    setTimeout(() => openModal("deletePasskeyModal"), 100);
  };

  // ============ Add Mobile flow ============
  const handleAddMobileStart = async () => {
    resetModalStates();
    setPasskeyVerificationResult(null);

    if (hasPasskey && passkeySupported) {
      const vr = await attemptSilentPasskeyVerification();
      if (vr) {
        setPasskeyVerificationResult(vr);
        setCurrentStep(2);
        openModal("addMobileModal");
        return;
      }
    }

    const methods = [];
    if (hasPasskey && passkeySupported) methods.push({ value: "passkey", label: "Passkey", icon: "ri-fingerprint-line", description: "Use Face ID, Touch ID, or Windows Hello" });
    if (hasGoogleAuth) methods.push({ value: "totp", label: "Google Authenticator", icon: "ri-shield-keyhole-line", description: "Use your authenticator app" });
    if (hasEmail) methods.push({ value: "email", label: "Email Verification", icon: "ri-mail-line", description: `Send code to ${maskEmail(emailID)}` });

    if (methods.length === 0) {
      alertErrorMessage("You need at least one verification method (Email, Google Auth, or Passkey) to add a mobile number");
      return;
    }

    setAddMobileAvailableMethods(methods);
    setAddMobileVerifyMethod(methods[0].value);
    setCurrentStep(1);
    openModal("addMobileModal");
  };

  const handleAddMobileVerifyIdentity = async () => {
    if (addMobileVerifyMethod === "passkey") {
      const vr = await handlePasskeyVerification();
      if (vr) {
        setPasskeyVerificationResult(vr);
        setCurrentStep(2);
      }
      return;
    }
    if (addMobileVerifyMethod === "totp") {
      if (!googleAuthCode || googleAuthCode.length !== 6) {
        alertErrorMessage("Please enter a valid 6-digit code");
        return;
      }
      setCurrentStep(2);
      return;
    }
    if (addMobileVerifyMethod === "email") {
      if (!emailOtpCode || emailOtpCode.length !== 6) {
        alertErrorMessage("Please enter a valid 6-digit OTP");
        return;
      }
      setCurrentStep(2);
    }
  };

  const handleAddMobileNext = () => {
    if (!newMobileNumber || newMobileNumber.length < 6) {
      alertErrorMessage("Please enter a valid mobile number");
      return;
    }
    const fullPhoneNumber = `${newCountryCode}${newMobileNumber}`;
    if (!isValidPhoneNumber(fullPhoneNumber)) {
      alertErrorMessage("Please enter a valid phone number for the selected country");
      return;
    }
    setCurrentStep(3);
  };

  const handleAddMobileComplete = async () => {
    if (!newMobileOtpCode || newMobileOtpCode.length !== 6) {
      alertErrorMessage("Please enter a valid 6-digit OTP");
      return;
    }
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const payload = {
        mobileNumber: newMobileNumber,
        countryCode: newCountryCode,
        mobileOtp: newMobileOtpCode,
      };
      if (passkeyVerificationResult) {
        payload.passkeyVerified = true;
        payload.passkeyUserId = passkeyVerificationResult.userId;
      } else if (addMobileVerifyMethod === "totp") {
        payload.tofaCode = googleAuthCode;
      } else if (addMobileVerifyMethod === "email") {
        payload.emailOtp = emailOtpCode;
      }
      const result = await AuthService.securityMobileAdd(payload);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Mobile number added successfully!");
        closeAllModals();
        handleUserDetails();
      } else {
        alertErrorMessage(result?.message || "Failed to add mobile number");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  // ============ Change Email flow ============
  const handleChangeEmailStart = () => {
    if (!canMakeSensitiveChanges()) {
      openModal("blockingModal");
      return;
    }
    setPendingAction("changeEmail");
    openModal("changeEmailConsentModal");
  };

  const handleChangeEmailProceed = async () => {
    closeModal("changeEmailConsentModal");
    resetModalStates();
    setPasskeyVerificationResult(null);

    if (hasPasskey && passkeySupported) {
      const vr = await attemptSilentPasskeyVerification();
      if (vr) {
        setPasskeyVerificationResult(vr);
        setCurrentStep(2);
        openModal("changeEmailModal");
        return;
      }
    }

    const methods = [];
    if (hasPasskey && passkeySupported) methods.push({ value: "passkey", label: "Passkey", icon: "ri-fingerprint-line", description: "Use Face ID, Touch ID, or Windows Hello" });
    if (hasGoogleAuth) methods.push({ value: "totp", label: "Google Authenticator", icon: "ri-shield-keyhole-line", description: "Use your authenticator app" });
    methods.push({ value: "email", label: "Email Verification", icon: "ri-mail-line", description: `Send code to ${maskEmail(emailID)}` });
    if (hasMobile) methods.push({ value: "mobile", label: "Mobile Verification", icon: "ri-smartphone-line", description: `Send code to ${maskPhone(mobileNumber)}` });

    setChangeAvailableMethods(methods);
    setChangeVerifyMethod(hasPasskey && passkeySupported ? "passkey" : (hasGoogleAuth ? "totp" : "email"));
    setCurrentStep(1);
    openModal("changeEmailModal");
  };

  const handleChangeEmailNextStep = async () => {
    if (currentStep === 1) {
      if (changeVerifyMethod === "passkey") {
        const vr = await handlePasskeyVerification();
        if (vr) {
          setPasskeyVerificationResult(vr);
          setCurrentStep(2);
        }
        return;
      }
      const codeMap = { totp: googleAuthCode, email: emailOtpCode, mobile: mobileOtpCode };
      const code = codeMap[changeVerifyMethod];
      if (!code || code.length !== 6) {
        alertErrorMessage("Please enter a valid 6-digit code");
        return;
      }
      setCurrentStep(2);
      return;
    }
    if (currentStep === 2) {
      if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
        alertErrorMessage("Please enter a valid email address");
        return;
      }
      try {
        setIsLoading(true);
        LoaderHelper.loaderStatus(true);
        const requestData = { newEmail };
        if (passkeyVerificationResult) {
          requestData.passkeyVerified = true;
          requestData.passkeyUserId = passkeyVerificationResult.userId;
        } else if (changeVerifyMethod === "totp") requestData.tofaCode = googleAuthCode;
        else if (changeVerifyMethod === "email") requestData.currentEmailOtp = emailOtpCode;
        else if (changeVerifyMethod === "mobile") requestData.currentMobileOtp = mobileOtpCode;

        const result = await AuthService.securityEmailChangeInitiate(requestData);
        if (result?.success) {
          alertSuccessMessage(result?.message || "Verification successful. Please send OTP to your new email.");
          setCurrentStep(3);
        } else {
          alertErrorMessage(result?.message || "Failed to initiate email change");
        }
      } catch (error) {
        alertErrorMessage(error?.message || "Something went wrong");
      } finally {
        setIsLoading(false);
        LoaderHelper.loaderStatus(false);
      }
      return;
    }
    if (currentStep === 3) {
      if (!newEmailOtpCode || newEmailOtpCode.length !== 6) {
        alertErrorMessage("Please enter a valid 6-digit OTP");
        return;
      }
      try {
        setIsLoading(true);
        LoaderHelper.loaderStatus(true);
        const result = await AuthService.securityEmailChangeComplete({ newEmailOtp: newEmailOtpCode });
        if (result?.success) {
          alertSuccessMessage(result?.message || "Email changed successfully!");
          closeAllModals();
          handleUserDetails();
        } else {
          alertErrorMessage(result?.message || "Failed to change email");
        }
      } catch (error) {
        alertErrorMessage(error?.message || "Something went wrong");
      } finally {
        setIsLoading(false);
        LoaderHelper.loaderStatus(false);
      }
    }
  };

  const handleOpenChangeEmailOptions = () => {
    closeModal("changeEmailModal");
    setTimeout(() => openModal("changeVerifyOptionsModal"), 100);
  };

  const handleSelectChangeMethod = (method) => {
    setChangeVerifyMethod(method.value);
    setEmailOtpCode("");
    setMobileOtpCode("");
    setGoogleAuthCode("");
    setResendTimer(0);
    setPasskeyVerificationResult(null);
    closeModal("changeVerifyOptionsModal");
    setTimeout(() => openModal("changeEmailModal"), 100);
  };

  const handleCloseChangeOptionsPopup = () => {
    closeModal("changeVerifyOptionsModal");
    setTimeout(() => openModal("changeEmailModal"), 100);
  };

  const getChangeVerificationTitle = () => {
    if (changeVerifyMethod === "passkey") return "Passkey Verification";
    if (changeVerifyMethod === "totp") return "Google Authenticator";
    if (changeVerifyMethod === "email") return "Email Verification";
    if (changeVerifyMethod === "mobile") return "Mobile Verification";
    return "Verify Your Identity";
  };

  const getChangeVerificationDescription = () => {
    if (changeVerifyMethod === "passkey") return "Use your passkey to verify your identity";
    if (changeVerifyMethod === "totp") return "Enter the 6-digit code from your authenticator app";
    if (changeVerifyMethod === "email") return `We'll send a verification code to ${maskEmail(emailID)}`;
    if (changeVerifyMethod === "mobile") return `We'll send a verification code to ${maskPhone(mobileNumber)}`;
    return "";
  };

  const getChangeOtpCode = () => {
    if (changeVerifyMethod === "totp") return googleAuthCode;
    if (changeVerifyMethod === "email") return emailOtpCode;
    if (changeVerifyMethod === "mobile") return mobileOtpCode;
    return "";
  };

  const setChangeOtpCode = (value) => {
    if (changeVerifyMethod === "totp") setGoogleAuthCode(value);
    else if (changeVerifyMethod === "email") setEmailOtpCode(value);
    else if (changeVerifyMethod === "mobile") setMobileOtpCode(value);
  };

  const sendChangeOtp = async () => {
    if (changeVerifyMethod === "passkey" || changeVerifyMethod === "totp") return true;
    const target = changeVerifyMethod === "email" ? "email" : "mobile";
    const sent = await handleSendOtp(target, "change_email");
    if (sent) setResendTimer(60);
    return sent;
  };

  // ============ Add Mobile helpers (sibling of change) ============
  const getAddMobileVerificationTitle = () => {
    if (addMobileVerifyMethod === "passkey") return "Passkey Verification";
    if (addMobileVerifyMethod === "totp") return "Google Authenticator";
    if (addMobileVerifyMethod === "email") return "Email Verification";
    return "Verify Your Identity";
  };

  const getAddMobileVerificationDescription = () => {
    if (addMobileVerifyMethod === "passkey") return "Use your passkey to verify your identity";
    if (addMobileVerifyMethod === "totp") return "Enter the 6-digit code from your authenticator app";
    if (addMobileVerifyMethod === "email") return `We'll send a verification code to ${maskEmail(emailID)}`;
    return "";
  };

  const getAddMobileOtpCode = () => {
    if (addMobileVerifyMethod === "totp") return googleAuthCode;
    if (addMobileVerifyMethod === "email") return emailOtpCode;
    return "";
  };

  const setAddMobileOtpCode = (value) => {
    if (addMobileVerifyMethod === "totp") setGoogleAuthCode(value);
    else if (addMobileVerifyMethod === "email") setEmailOtpCode(value);
  };

  const sendAddMobileOtp = async () => {
    if (addMobileVerifyMethod === "passkey" || addMobileVerifyMethod === "totp") return true;
    const sent = await handleSendOtp("email", "add_mobile");
    if (sent) setResendTimer(60);
    return sent;
  };

  const handleOpenAddMobileOptions = () => {
    closeModal("addMobileModal");
    setTimeout(() => openModal("addMobileVerifyOptionsModal"), 100);
  };

  const handleSelectAddMobileMethod = (method) => {
    setAddMobileVerifyMethod(method.value);
    setEmailOtpCode("");
    setGoogleAuthCode("");
    setResendTimer(0);
    setPasskeyVerificationResult(null);
    closeModal("addMobileVerifyOptionsModal");
    setTimeout(() => openModal("addMobileModal"), 100);
  };

  const handleCloseAddMobileOptionsPopup = () => {
    closeModal("addMobileVerifyOptionsModal");
    setTimeout(() => openModal("addMobileModal"), 100);
  };

  // ============ Change Mobile flow ============
  const handleChangeMobileStart = () => {
    if (!canMakeSensitiveChanges()) {
      openModal("blockingModal");
      return;
    }
    setPendingAction("changeMobile");
    openModal("changeMobileConsentModal");
  };

  const handleChangeMobileProceed = async () => {
    closeModal("changeMobileConsentModal");
    resetModalStates();
    setPasskeyVerificationResult(null);

    if (hasPasskey && passkeySupported) {
      const vr = await attemptSilentPasskeyVerification();
      if (vr) {
        setPasskeyVerificationResult(vr);
        setCurrentStep(2);
        openModal("changeMobileModal");
        return;
      }
    }

    const methods = [];
    if (hasPasskey && passkeySupported) methods.push({ value: "passkey", label: "Passkey", icon: "ri-fingerprint-line", description: "Use Face ID, Touch ID, or Windows Hello" });
    if (hasGoogleAuth) methods.push({ value: "totp", label: "Google Authenticator", icon: "ri-shield-keyhole-line", description: "Use your authenticator app" });
    if (hasEmail) methods.push({ value: "email", label: "Email Verification", icon: "ri-mail-line", description: `Send code to ${maskEmail(emailID)}` });
    methods.push({ value: "mobile", label: "Mobile Verification", icon: "ri-smartphone-line", description: `Send code to ${maskPhone(mobileNumber)}` });

    setChangeAvailableMethods(methods);
    setChangeVerifyMethod(hasPasskey && passkeySupported ? "passkey" : (hasGoogleAuth ? "totp" : (hasEmail ? "email" : "mobile")));
    setCurrentStep(1);
    openModal("changeMobileModal");
  };

  const handleChangeMobileNextStep = async () => {
    if (currentStep === 1) {
      if (changeVerifyMethod === "passkey") {
        const vr = await handlePasskeyVerification();
        if (vr) {
          setPasskeyVerificationResult(vr);
          setCurrentStep(2);
        }
        return;
      }
      const codeMap = { totp: googleAuthCode, email: emailOtpCode, mobile: mobileOtpCode };
      const code = codeMap[changeVerifyMethod];
      if (!code || code.length !== 6) {
        alertErrorMessage("Please enter a valid 6-digit code");
        return;
      }
      setCurrentStep(2);
      return;
    }
    if (currentStep === 2) {
      if (!newMobileNumber || newMobileNumber.length < 6) {
        alertErrorMessage("Please enter a valid mobile number");
        return;
      }
      const fullPhoneNumber = `${newCountryCode}${newMobileNumber}`;
      if (!isValidPhoneNumber(fullPhoneNumber)) {
        alertErrorMessage("Please enter a valid phone number for the selected country");
        return;
      }
      try {
        setIsLoading(true);
        LoaderHelper.loaderStatus(true);
        const requestData = { newMobileNumber, newCountryCode };
        if (passkeyVerificationResult) {
          requestData.passkeyVerified = true;
          requestData.passkeyUserId = passkeyVerificationResult.userId;
        } else if (changeVerifyMethod === "totp") requestData.tofaCode = googleAuthCode;
        else if (changeVerifyMethod === "email") requestData.currentEmailOtp = emailOtpCode;
        else if (changeVerifyMethod === "mobile") requestData.currentMobileOtp = mobileOtpCode;

        const result = await AuthService.securityMobileChangeInitiate(requestData);
        if (result?.success) {
          alertSuccessMessage(result?.message || "Verification successful. Please send OTP to your new mobile.");
          setCurrentStep(3);
        } else {
          alertErrorMessage(result?.message || "Failed to initiate mobile change");
        }
      } catch (error) {
        alertErrorMessage(error?.message || "Something went wrong");
      } finally {
        setIsLoading(false);
        LoaderHelper.loaderStatus(false);
      }
      return;
    }
    if (currentStep === 3) {
      if (!newMobileOtpCode || newMobileOtpCode.length !== 6) {
        alertErrorMessage("Please enter a valid 6-digit OTP");
        return;
      }
      try {
        setIsLoading(true);
        LoaderHelper.loaderStatus(true);
        const result = await AuthService.securityMobileChangeComplete({ newMobileOtp: newMobileOtpCode });
        if (result?.success) {
          alertSuccessMessage(result?.message || "Mobile number changed successfully!");
          closeAllModals();
          handleUserDetails();
        } else {
          alertErrorMessage(result?.message || "Failed to change mobile number");
        }
      } catch (error) {
        alertErrorMessage(error?.message || "Something went wrong");
      } finally {
        setIsLoading(false);
        LoaderHelper.loaderStatus(false);
      }
    }
  };

  const handleOpenChangeMobileOptions = () => {
    closeModal("changeMobileModal");
    setTimeout(() => openModal("changeMobileVerifyOptionsModal"), 100);
  };

  const handleSelectChangeMobileMethod = (method) => {
    setChangeVerifyMethod(method.value);
    setEmailOtpCode("");
    setMobileOtpCode("");
    setGoogleAuthCode("");
    setResendTimer(0);
    setPasskeyVerificationResult(null);
    closeModal("changeMobileVerifyOptionsModal");
    setTimeout(() => openModal("changeMobileModal"), 100);
  };

  const handleCloseChangeMobileOptionsPopup = () => {
    closeModal("changeMobileVerifyOptionsModal");
    setTimeout(() => openModal("changeMobileModal"), 100);
  };

  // ============ Add Email flow (for mobile-only users) ============
  const handleAddEmailStart = async () => {
    resetModalStates();
    setPasskeyVerificationResult(null);

    if (hasPasskey && passkeySupported) {
      const vr = await attemptSilentPasskeyVerification();
      if (vr) {
        setPasskeyVerificationResult(vr);
        setAddEmailStep(3);
        openModal("addEmailModal");
        return;
      }
    }

    if (hasGoogleAuth) {
      setAddEmailStep(1);
      openModal("addEmailModal");
    } else if (hasMobile) {
      setAddEmailStep(2);
      openModal("addEmailModal");
    } else {
      alertErrorMessage("No verification method available");
    }
  };

  const handleAddEmailNextStep = async () => {
    if (addEmailStep === 1 && hasGoogleAuth) {
      if (!googleAuthCode || googleAuthCode.length !== 6) {
        alertErrorMessage("Please enter a valid 6-digit code");
        return;
      }
      setAddEmailStep(hasMobile ? 2 : 3);
      return;
    }
    if (addEmailStep === 2) {
      if (!mobileOtpCode || mobileOtpCode.length !== 6) {
        alertErrorMessage("Please enter a valid 6-digit OTP");
        return;
      }
      setAddEmailStep(3);
      return;
    }
    if (addEmailStep === 3) {
      if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
        alertErrorMessage("Please enter a valid email address");
        return;
      }
      setAddEmailStep(4);
      return;
    }
    if (addEmailStep === 4) {
      if (!newEmailOtpCode || newEmailOtpCode.length !== 6) {
        alertErrorMessage("Please enter a valid 6-digit OTP");
        return;
      }
      try {
        setIsLoading(true);
        LoaderHelper.loaderStatus(true);
        const result = await AuthService.securityEmailAdd({
          email: newEmail,
          tofaCode: hasGoogleAuth ? googleAuthCode : undefined,
          mobileOtp: mobileOtpCode,
          emailOtp: newEmailOtpCode,
        });
        if (result?.success) {
          alertSuccessMessage(result?.message || "Email added successfully!");
          closeAllModals();
          handleUserDetails();
        } else {
          alertErrorMessage(result?.message || "Failed to add email");
        }
      } catch (error) {
        alertErrorMessage(error?.message || "Something went wrong");
      } finally {
        setIsLoading(false);
        LoaderHelper.loaderStatus(false);
      }
    }
  };

  const handleGoBack = (flowType) => {
    if (flowType === "addMobile" && currentStep > 1) setCurrentStep(currentStep - 1);
    else if (flowType === "changeEmail" && currentStep > 1) setCurrentStep(currentStep - 1);
    else if (flowType === "changeMobile" && currentStep > 1) setCurrentStep(currentStep - 1);
    else if (flowType === "addEmail") {
      if (addEmailStep > 1) {
        if (addEmailStep === 2 && !hasGoogleAuth) closeAllModals();
        else setAddEmailStep(addEmailStep - 1);
      }
    } else if (flowType === "googleAuthQr") switchModal("googleAuthQrModal", "googleAuthSetupModal");
    else if (flowType === "googleAuthVerify") switchModal("googleAuthVerifyModal", "googleAuthQrModal");
  };

  useEffect(() => {
    if (!location.state?.openLogin2Step) return;
    setIsLogin2StepOpen(true);
    void syncLogin2StepFromApi();
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, syncLogin2StepFromApi]);

  const openGoogleVerify = (action = "enable", target = "login") => {
    setGoogleToggleAction(action);
    setGoogleVerifyTarget(target);
    setGoogleOtp(["", "", "", "", "", ""]);
    setGoogleVerifyOpen(true);
    window.requestAnimationFrame(() => {
      document.getElementById("ga-otp-0")?.focus();
    });
  };

  const closeGoogleVerify = () => {
    setGoogleVerifyOpen(false);
    setGoogleOtp(["", "", "", "", "", ""]);
  };

  const handleGoogleOtpChange = (idx, e) => {
    const digit = String(e.target.value ?? "").replace(/\D/g, "").slice(-1);
    setGoogleOtp((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < 5) {
      window.requestAnimationFrame(() => {
        document.getElementById(`ga-otp-${idx + 1}`)?.focus();
      });
    }
  };

  const handleGoogleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !googleOtp[idx] && idx > 0) {
      window.requestAnimationFrame(() => {
        document.getElementById(`ga-otp-${idx - 1}`)?.focus();
      });
    }
  };

  const googleOtpComplete = useMemo(() => googleOtp.every((d) => d && d.length === 1), [googleOtp]);

  const pasteGoogleOtp = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const digits = String(raw || "").replace(/\D/g, "").slice(0, 6).split("");
      if (digits.length !== 6) return;
      setGoogleOtp(digits);
      window.requestAnimationFrame(() => {
        document.getElementById("ga-otp-5")?.focus();
      });
    } catch {
      // ignore
    }
  };

  const confirmGoogleOtp = async () => {
    if (!googleOtpComplete) return;
    if (googleVerifyTarget === "wds" && wdsPendingToggle) {
      try {
        LoaderHelper.loaderStatus(true);
        const res = await verifyAndToggleWithdrawalSetting({ method: wdsPendingToggle.method, enable: wdsPendingToggle.enable, code: googleOtp.join("") });
        LoaderHelper.loaderStatus(false);
        if (res?.success) {
          setWithdrawSettings((s) => ({ ...s, googleAuthenticator: wdsPendingToggle.enable }));
          alertSuccessMessage(res.message || "Setting updated.");
          setWdsPendingToggle(null);
          closeGoogleVerify();
        } else {
          alertErrorMessage(res?.message || "Verification failed.");
        }
      } catch { LoaderHelper.loaderStatus(false); alertErrorMessage("Something went wrong."); }
      return;
    }
    const result = await updateTwoLogin2StepStatus("totp", googleOtp.join(""), googleToggleAction);
    if (isTotpNotConfiguredMessage(result?.message)) {
      closeGoogleVerify();
      await applyGoogleAuthRemoved(result, { showNotice: false });
      if (googleToggleAction === "enable") {
        setIsLogin2StepOpen(false);
        window.setTimeout(() => openBindPrompt("googleAuth"), 0);
      } else {
        alertSuccessMessage("Google Authenticator is already removed. It is no longer used for login.");
      }
      return;
    }
    if (!result?.success) return;
    setLogin2Step((s) => ({ ...s, googleAuthenticator: googleToggleAction === "enable" }));
    closeGoogleVerify();
  };

  const openSmsVerify = (action = "enable", target = "login") => {
    setSmsToggleAction(action);
    setSmsVerifyTarget(target);
    setSmsOtp(["", "", "", "", "", ""]);
    setSmsVerifyOpen(true);
    window.requestAnimationFrame(() => {
      document.getElementById("sms-otp-0")?.focus();
    });
  };

  const closeSmsVerify = () => {
    setSmsVerifyOpen(false);
    setSmsOtp(["", "", "", "", "", ""]);
  };

  const handleSmsOtpChange = (idx, e) => {
    const digit = String(e.target.value ?? "").replace(/\D/g, "").slice(-1);
    setSmsOtp((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < 5) {
      window.requestAnimationFrame(() => {
        document.getElementById(`sms-otp-${idx + 1}`)?.focus();
      });
    }
  };

  const handleSmsOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !smsOtp[idx] && idx > 0) {
      window.requestAnimationFrame(() => {
        document.getElementById(`sms-otp-${idx - 1}`)?.focus();
      });
    }
  };

  const smsOtpComplete = useMemo(() => smsOtp.every((d) => d && d.length === 1), [smsOtp]);

  const pasteSmsOtp = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const digits = String(raw || "").replace(/\D/g, "").slice(0, 6).split("");
      if (digits.length !== 6) return;
      setSmsOtp(digits);
      window.requestAnimationFrame(() => {
        document.getElementById("sms-otp-5")?.focus();
      });
    } catch {
      // ignore
    }
  };

  const confirmSmsOtp = async () => {
    if (!smsOtpComplete) return;
    if (smsVerifyTarget === "wds" && wdsPendingToggle) {
      try {
        LoaderHelper.loaderStatus(true);
        const res = await verifyAndToggleWithdrawalSetting({ method: wdsPendingToggle.method, enable: wdsPendingToggle.enable, code: smsOtp.join("") });
        LoaderHelper.loaderStatus(false);
        if (res?.success) {
          setWithdrawSettings((s) => ({ ...s, smsVerification: wdsPendingToggle.enable }));
          alertSuccessMessage(res.message || "Setting updated.");
          setWdsPendingToggle(null);
          closeSmsVerify();
        } else {
          alertErrorMessage(res?.message || "Verification failed.");
        }
      } catch { LoaderHelper.loaderStatus(false); alertErrorMessage("Something went wrong."); }
      return;
    }
    const result = await updateTwoLogin2StepStatus("phone", smsOtp.join(""), smsToggleAction);
    if (!result?.success) return;
    setLogin2Step((s) => ({ ...s, smsVerification: smsToggleAction === "enable" }));
    closeSmsVerify();
  };

  const openEmailVerify = (target = "login", action = "enable") => {
    setEmailOtp(["", "", "", "", "", ""]);
    setEmailResendLeft(53);
    setEmailVerifyTarget(target);
    setEmailToggleAction(action);
    setEmailVerifyOpen(true);
    if (target === "login") {
      void sendOtpForLogin2Step("email");
    }
    window.requestAnimationFrame(() => {
      document.getElementById("ev-otp-0")?.focus();
    });
  };

  const closeEmailVerify = () => {
    setEmailVerifyOpen(false);
    setEmailOtp(["", "", "", "", "", ""]);
  };

  const handleEmailOtpChange = (idx, e) => {
    const digit = String(e.target.value ?? "").replace(/\D/g, "").slice(-1);
    setEmailOtp((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < 5) {
      window.requestAnimationFrame(() => {
        document.getElementById(`ev-otp-${idx + 1}`)?.focus();
      });
    }
  };

  const handleEmailOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !emailOtp[idx] && idx > 0) {
      window.requestAnimationFrame(() => {
        document.getElementById(`ev-otp-${idx - 1}`)?.focus();
      });
    }
  };

  const emailOtpComplete = useMemo(() => emailOtp.every((d) => d && d.length === 1), [emailOtp]);

  const pasteEmailOtp = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const digits = String(raw || "").replace(/\D/g, "").slice(0, 6).split("");
      if (digits.length !== 6) return;
      setEmailOtp(digits);
      window.requestAnimationFrame(() => {
        document.getElementById("ev-otp-5")?.focus();
      });
    } catch {
      // ignore
    }
  };

  const confirmEmailOtp = async () => {
    if (!emailOtpComplete) return;
    if (emailVerifyTarget === "wds" && wdsPendingToggle) {
      try {
        LoaderHelper.loaderStatus(true);
        const res = await verifyAndToggleWithdrawalSetting({ method: wdsPendingToggle.method, enable: wdsPendingToggle.enable, code: emailOtp.join("") });
        LoaderHelper.loaderStatus(false);
        if (res?.success) {
          setWithdrawSettings((s) => ({ ...s, emailVerification: wdsPendingToggle.enable }));
          alertSuccessMessage(res.message || "Setting updated.");
          setWdsPendingToggle(null);
          closeEmailVerify();
        } else {
          alertErrorMessage(res?.message || "Verification failed.");
        }
      } catch { LoaderHelper.loaderStatus(false); alertErrorMessage("Something went wrong."); }
      return;
    }
    const result = await updateTwoLogin2StepStatus("email", emailOtp.join(""), emailToggleAction);
    if (!result?.success) return;
    setLogin2Step((s) => ({ ...s, emailVerification: emailToggleAction === "enable" }));
    closeEmailVerify();
  };

  useEffect(() => {
    if (!emailVerifyOpen) return undefined;
    const id = setInterval(() => {
      setEmailResendLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [emailVerifyOpen]);

  const hasBoundPhone = useMemo(() => {
    const candidates = [
      userDetails?.mobileNumber,
      userDetails?.phoneNumber,
      userDetails?.phone,
      userDetails?.mobile,
      userDetails?.mobile_number,
      userDetails?.phone_number,
    ].filter(Boolean);
    if (!candidates.length) return false;
    const v = String(candidates[0]).trim();
    return v.length >= 6;
  }, [userDetails]);

  const hasFundPassword = useMemo(() => {
    const v =
      userDetails?.fundPassword ??
      userDetails?.fund_password ??
      userDetails?.payPin ??
      userDetails?.pay_pin ??
      userDetails?.isFundPasswordSet ??
      userDetails?.is_fund_password_set ??
      userDetails?.tradingPassword ??
      userDetails?.trading_password;
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v === 1;
    if (typeof v === "string") return v.trim().length > 0 && v !== "0";
    return false;
  }, [userDetails]);

  const effectiveHasFundPassword = fundPasswordStatus !== null ? fundPasswordStatus : hasFundPassword;

  // ── Fetch withdrawal security settings from backend on mount ────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithdrawalSecuritySettings();
        if (cancelled || !res?.success) return;
        const s = res.data?.settings;
        if (!s) return;
        setWithdrawSettings((prev) => ({
          ...prev,
          emailVerification: !!s.methods?.email?.enabled,
          smsVerification: !!s.methods?.mobile?.enabled,
          googleAuthenticator: !!s.methods?.google_authenticator?.enabled,
          fundPassword: !!s.methods?.fund_password?.enabled,
          passkeyEnabled: !!s.passkey_enabled,
        }));
        setWithdrawSettingsLoaded(true);
      } catch {
        // ignore — keep defaults
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await AuthService.getConnectedAccountCount();
        if (cancelled || !res?.success) return;
        const raw = res.data;
        const n = typeof raw === "number" ? raw : Number(raw);
        setConnectedAccountCount(Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0);
      } catch {
        if (!cancelled) setConnectedAccountCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await AuthService.getEmergencyContactCount();
        if (cancelled || !res?.success) return;
        const raw = res.data;
        const n = typeof raw === "number" ? raw : Number(raw);
        setEmergencyContactCount(Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0);
      } catch {
        if (!cancelled) setEmergencyContactCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep fund password toggle in sync with whether fund password is actually set.
  useEffect(() => {
    if (!withdrawSettingsLoaded) {
      setWithdrawSettings((s) => ({ ...s, fundPassword: !!effectiveHasFundPassword }));
    }
  }, [effectiveHasFundPassword, withdrawSettingsLoaded]);

  /** Save a single withdrawal security setting to the backend (direct, no verify). */
  const saveWithdrawSetting = useCallback(async (patch) => {
    try {
      await updateWithdrawalSecuritySettings(patch);
    } catch {
      // silent — UI already updated optimistically; revert handled per-toggle below
    }
  }, []);

  /**
   * Open the appropriate existing verification modal for a withdrawal-settings toggle.
   * passkey_enabled is hardware-verified at login — toggle directly.
   */
  const handleWdsToggle = useCallback((method, enable) => {
    // Core-method enforcement: at least one of email / mobile / google_authenticator must remain enabled.
    // fund_password and passkey are supplements only.
    if (!enable && ["email", "mobile", "google_authenticator"].includes(method)) {
      const s = withdrawSettings;
      const coreEnabled = {
        email: s.emailVerification,
        mobile: s.smsVerification,
        google_authenticator: s.googleAuthenticator,
      };
      const remainingCore = Object.entries(coreEnabled).filter(([k, v]) => k !== method && v).length;
      if (remainingCore === 0) {
        alertErrorMessage("At least one of email, mobile, or Google Authenticator must remain enabled for withdrawals.");
        return;
      }
    }

    setWdsPendingToggle({ method, enable });
    if (method === "email") {
      setEmailOtp(["", "", "", "", "", ""]);
      setEmailResendLeft(53);
      setEmailVerifyTarget("wds");
      setEmailToggleAction(enable ? "enable" : "disable");
      setEmailVerifyOpen(true);
      sendWithdrawalVerificationOtp("email").catch(() => { });
      window.requestAnimationFrame(() => { document.getElementById("ev-otp-0")?.focus(); });
    } else if (method === "mobile") {
      openSmsVerify(enable ? "enable" : "disable", "wds");
      sendWithdrawalVerificationOtp("mobile").catch(() => { });
    } else if (method === "google_authenticator") {
      openGoogleVerify(enable ? "enable" : "disable", "wds");
    } else if (method === "fund_password") {
      setWdsFundPwInput("");
      setWdsFundPwError("");
      setWdsFundPwOpen(true);
    }
  }, [saveWithdrawSetting, withdrawSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Submit fund-password verification for withdrawal settings toggle. */
  const submitWdsFundPw = useCallback(async () => {
    if (!wdsPendingToggle || !wdsFundPwInput.trim()) return;
    setWdsFundPwError("");
    try {
      LoaderHelper.loaderStatus(true);
      const res = await verifyAndToggleWithdrawalSetting({ method: "fund_password", enable: wdsPendingToggle.enable, fund_password: wdsFundPwInput });
      LoaderHelper.loaderStatus(false);
      if (res?.success) {
        setWithdrawSettings((s) => ({ ...s, fundPassword: wdsPendingToggle.enable }));
        alertSuccessMessage(res.message || "Setting updated.");
        setWdsPendingToggle(null);
        setWdsFundPwOpen(false);
        setWdsFundPwInput("");
      } else {
        setWdsFundPwError(res?.code === "MIN_ONE_METHOD_REQUIRED"
          ? "At least one withdrawal verification method must remain enabled."
          : res?.message || "Verification failed.");
      }
    } catch {
      LoaderHelper.loaderStatus(false);
      setWdsFundPwError("Something went wrong. Please try again.");
    }
  }, [wdsPendingToggle, wdsFundPwInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasTrustedAddresses = useMemo(() => {
    const candidates = [
      userDetails?.trustedAddresses,
      userDetails?.trusted_addresses,
      userDetails?.withdrawAddressBook,
      userDetails?.withdraw_address_book,
      userDetails?.addressBook,
      userDetails?.address_book,
      userDetails?.whitelistAddresses,
      userDetails?.whitelist_addresses,
    ].filter(Boolean);
    if (!candidates.length) return false;
    const v = candidates[0];
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "number") return v > 0;
    if (typeof v === "boolean") return v;
    if (typeof v === "string") return v.trim().length > 0 && v !== "0";
    return false;
  }, [userDetails]);

  // ============ Button handlers (email is root prereq for adding any method) ============
  const requireEmailFirst = (messageSuffix) => {
    if (!hasEmail) {
      alertErrorMessage(`Please verify your email first before ${messageSuffix}.`);
      go("/user_profile/security/emailVerification");
      return true;
    }
    return false;
  };

  const gotoPasskey = () => {
    if (hasPasskey) {
      go("/user_profile/security/passkey");
      return;
    }
    if (requireEmailFirst("adding a passkey")) return;
    go("/user_profile/security/passkey");
  };

  const gotoGoogleAuth = () => {
    if (hasGoogleAuth) {
      // Disable with email / mobile / passkey / TOTP (recovery when authenticator is lost)
      openDisableGA();
      return;
    }
    if (requireEmailFirst("enabling Google Authenticator")) return;
    go("/user_profile/security/bindGoogleAuthenticator");
  };

  const gotoAddOrChangeMobile = () => {
    if (hasMobile) {
      // Change flow: still requires email as a recovery/verification root
      if (requireEmailFirst("changing your mobile number")) return;
    } else {
      if (requireEmailFirst("adding a mobile number")) return;
    }
    go("/user_profile/security/smsVerification");
  };

  const gotoAddOrChangeEmail = () => {
    go("/user_profile/security/emailVerification");
  };

  const openBindPrompt = useCallback((type) => {
    if (type === "googleAuth") {
      setBindPrompt({
        open: true,
        title: "Bind Google Authenticator",
        description: "Google Authenticator is not set up on this account. Bind it first, then you can use it for Login 2-Step.",
        route: "/user_profile/security/bindGoogleAuthenticator",
      });
      return;
    }
    setBindPrompt({
      open: true,
      title: "Phone Unbound",
      description: "Phone verification is available only after the phone is bound.",
      route: "/user_profile/security/smsVerification",
    });
  }, []);

  return (
    <>
      <div className="dashboard_right">
        <div className="tf-sec-page">
          <div className="tf-sec-page__hero">
            <div className="tf-sec-page__hero-content">
              <h1 className="tf-sec-page__hero-title">Security Settings</h1>
              <p className="tf-sec-page__hero-description">
                Take full control of your account security with advanced verification options like passkeys, email OTP, and mobile
                authentication for a safer experience.
              </p>
            </div>
            <div className="tf-sec-page__hero-media">
              <img className="dark_img" src="/images/security/417923014-aaa37ef0-270c-49ed-99d0-02b7e5924263-1.svg" alt="" />
              <img className="light_img" src="/images/security_lock_light.svg" alt="" />
            </div>
          </div>

          {securityOverviewReady ? (
            <SecurityLevelOverview
              items={securityLevelItems}
              onInactiveClick={(id) => {
                switch (id) {
                  case "passkey":
                    gotoPasskey();
                    break;
                  case "google":
                    gotoGoogleAuth();
                    break;
                  case "email":
                    gotoAddOrChangeEmail();
                    break;
                  case "sms":
                    gotoAddOrChangeMobile();
                    break;
                  case "antiPhishing":
                    go("/user_profile/security/antiPhishing");
                    break;
                  case "login2step":
                    setIsLogin2StepOpen(true);
                    break;
                  default:
                    break;
                }
              }}
            />
          ) : null}

          <section className="tf-sec-page__card">
            <div className="tf-sec-page__card-header">
              <h2 className="tf-sec-page__card-title">Basic</h2>
              <p className="tf-sec-page__card-subtitle">
                Essential verification methods to secure your account and protect your assets.
              </p>
            </div>
            <div className="tf-sec-page__card-body">
              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container4.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Passkeys</span>
                      <span className="tf-sec-page__badge">Recommended</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Enables secure, passwordless authentication using device-based credentials. Provides faster logins and stronger
                      protection against phishing and unauthorized access.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  <div className={`tf-sec-page__status${hasPasskey ? " tf-sec-page__status--success" : ""}`}>
                    <div className="tf_checkfill">{hasPasskey ? <i className="ri-check-fill"></i> : <i className="ri-close-fill" />}</div>
                  </div>
                </div>
                <div className="tf-sec-page__row-actions">
                  <button type="button" className="tf-sec-page__btn" onClick={gotoPasskey}>
                    {hasPasskey ? "Manage" : "Turn on"}
                  </button>
                </div>
              </div>

              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container8.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Google Authenticator</span>
                      <span className="tf-sec-page__badge">Recommended</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Generates time-based one-time codes for secure login verification. Adds an extra layer of protection beyond
                      passwords to prevent unauthorized access.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  <div className={`tf-sec-page__status${hasGoogleAuth ? " tf-sec-page__status--success" : ""}`}>
                    <div className="tf_checkfill">{hasGoogleAuth ? <i className="ri-check-fill"></i> : <i className="ri-close-fill" />}</div>
                  </div>
                </div>
                <div className="tf-sec-page__row-actions">
                  <button type="button" className="tf-sec-page__btn" onClick={gotoGoogleAuth}>
                    {hasGoogleAuth ? "Disable" : "Turn on"}
                  </button>
                </div>
              </div>

              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container9.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Email Verification</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Securely verifies user identity via email confirmation. Bound email is required before other methods.
                      Enable Login 2-Step below to require email OTP at sign-in.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  {hasEmail ? (
                    <span className="tf-sec-page__status-email tf-sec-page__status-email--success">
                      {maskEmail(emailID)}
                    </span>
                  ) : (
                    <div className="tf-sec-page__status">
                      <div className="tf_checkfill">{hasEmail ? <i className="ri-check-fill"></i> : <i className="ri-close-fill" />}</div>
                    </div>
                  )}
                </div>
                <div className="tf-sec-page__row-actions">
                  <button type="button" className="tf-sec-page__btn" onClick={gotoAddOrChangeEmail}>
                    {hasEmail ? "Change" : "Turn on"}
                  </button>
                </div>
              </div>

              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container6.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Phone Verification</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Securely verifies user identity using SMS-based OTP. Bound phone is required for SMS recovery.
                      Enable Login 2-Step below to require SMS OTP at sign-in.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  {hasMobile ? (
                    <span className="tf-sec-page__status-email tf-sec-page__status-email--success">
                      {maskPhone(mobileNumber)}
                    </span>
                  ) : (
                    <div className="tf-sec-page__status">
                      <div className="tf_checkfill">{hasMobile ? <i className="ri-check-fill"></i> : <i className="ri-close-fill" />}</div>
                    </div>
                  )}
                </div>
                <div className="tf-sec-page__row-actions">
                  <button type="button" className="tf-sec-page__btn" onClick={gotoAddOrChangeMobile}>
                    {hasMobile ? "Change" : "Turn on"}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="tf-sec-page__card">
            <div className="tf-sec-page__card-header">
              <h2 className="tf-sec-page__card-title">Password Management</h2>
            </div>
            <div className="tf-sec-page__card-body">
              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container1.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Password</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Protects account access with secure authentication. Helps prevent unauthorized logins.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-actions">
                  <button type="button" className="tf-sec-page__btn" onClick={handlePasswordChangeClick}>
                    Change
                  </button>
                </div>
              </div>

              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container11.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Fund Password</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Adds an extra password layer for transactions and withdrawals. Ensures stronger protection for funds against
                      unauthorized actions.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  <div className={`tf-sec-page__status ${effectiveHasFundPassword ? "tf-sec-page__status--success" : ""}`}>
                    <div className="tf_checkfill">
                      {effectiveHasFundPassword ? <i className="ri-check-fill" /> : <i className="ri-close-fill" />}
                    </div>
                  </div>
                </div>
                <div className="tf-sec-page__row-actions">
                  <button
                    type="button"
                    className="tf-sec-page__btn"
                    onClick={() => setFundPasswordChangeConfirmOpen(true)}
                  >
                    {effectiveHasFundPassword ? "Change" : "Setup"}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="tf-sec-page__card">
            <div className="tf-sec-page__card-header">
              <h2 className="tf-sec-page__card-title">Advanced Security</h2>
            </div>
            <div className="tf-sec-page__card-body">
              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container14.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Login 2-Step Verification</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Adds a second verification step for secure login. Protects accounts even if the password is compromised.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  <div className={`tf-sec-page__status${login2StepRowAnyOn ? " tf-sec-page__status--success" : ""}`}>
                    {login2StepRowAnyOn ? (
                      <div className="tf-sec-page__l2sv-row-icons" aria-label="Active login 2-step methods">
                        {login2StepRowFlags.googleAuthenticator ? (
                          <span className="tf-sec-page__l2sv-method" title="Google Authenticator">
                            <img src="/images/security/verification_icon.svg" alt="" />
                          </span>
                        ) : null}
                        {login2StepRowFlags.emailVerification ? (
                          <span className="tf-sec-page__l2sv-method" title="Email verification">
                            <img src="/images/security/verification_icon3.svg" alt="" />
                          </span>
                        ) : null}
                        {login2StepRowFlags.smsVerification ? (
                          <span className="tf-sec-page__l2sv-method" title="Phone (SMS) verification">
                            <img src="/images/security/verification_icontwo.svg" alt="" />
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="tf_checkfill">{login2StepRowAnyOn ? <i className="ri-check-fill"></i> : <i className="ri-close-fill" />}</div>
                    {/* <span>{login2StepRowAnyOn ? "on" : "off"}</span> */}
                  </div>
                </div>
                <div className="tf-sec-page__row-actions">
                  <button
                    type="button"
                    className="tf-sec-page__btn"
                    onClick={() => {
                      setIsLogin2StepOpen(true);
                      void syncLogin2StepFromApi();
                    }}
                  >
                    {login2StepRowAnyOn ? "Change" : "Turn on"}
                  </button>
                </div>
              </div>

              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container13.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Anti-Phishing Code</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Adds a personal code to verify authentic messages. Helps users identify and avoid phishing attempts.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  <div className={`tf-sec-page__status${hasAntiPhishingCode ? " tf-sec-page__status--success" : ""}`}>
                    <div className="tf_checkfill">{hasAntiPhishingCode ? <i className="ri-check-fill"></i> : <i className="ri-close-fill" />}</div>
                    <span className={hasAntiPhishingCode ? "tf-sec-page__fund-pw-status tf-sec-page__fund-pw-status--on" : "tf-sec-page__fund-pw-status"}>
                      {/* {hasAntiPhishingCode ? "on" : "off"} */}
                    </span>
                  </div>
                </div>
                <div className="tf-sec-page__row-actions tf-sec-page__row-actions--split">
                  <button type="button" className="tf-sec-page__btn" onClick={() => go("/user_profile/security/antiPhishing")}>
                    {hasAntiPhishingCode ? "Change" : "Turn on"}
                  </button>
                  {hasAntiPhishingCode ? (
                    <button type="button" className="tf-sec-page__btn tf-sec-page__btn--danger" onClick={openAntiPhishingRemove}>
                      Turn off
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">

                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container3.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Withdrawal Settings</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Manages withdrawal options and security controls. Ensures safe and authorized fund transfers.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">

                  <div className={`tf-sec-page__status ${withdrawSettings?.emailVerification || withdrawSettings?.smsVerification || withdrawSettings?.googleAuthenticator ? "tf-sec-page__status--success" : ""}`}>
                    <div className="email_verify">
                      {/* <img src="/images/emailicon2.svg" alt="" /> */}
                    </div>
                    <div className={`tf_checkfill `}>{withdrawSettings?.emailVerification || withdrawSettings?.smsVerification || withdrawSettings?.googleAuthenticator ? <i className="ri-check-fill"></i> : <i className="ri-close-fill" />}</div>
                    {/* <span>off</span> */}
                  </div>
                </div>
                <div className="tf-sec-page__row-actions">
                  <button type="button" className="tf-sec-page__btn" onClick={() => setIsWithdrawSettingsOpen(true)}>
                    Change
                  </button>
                </div>
              </div>

              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container10.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Emergency Contact</span>
                    </div>
                    <p className="tf-sec-page__row-description">Adds a trusted contact for account recovery. Helps in case of emergencies.</p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  <div className={`tf-sec-page__status${emergencyContactCount > 0 ? " tf-sec-page__status--success" : ""}`}>
                    <div className="tf_checkfill">
                      {emergencyContactCount > 0 ? <i className="ri-check-fill" /> : <i className="ri-close-fill" />}
                    </div>
                  </div>
                </div>
                <div className="tf-sec-page__row-actions">
                  <button type="button" className="tf-sec-page__btn" onClick={() => go("/user_profile/security/emergencyContact")}>
                    {emergencyContactCount > 0 ? "Manage" : "Turn on"}
                  </button>
                </div>
              </div>

              {/* <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container5.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Account Connections</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Manages linked accounts and services. Helps control access and maintain security.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  <div className={`tf-sec-page__status${connectedAccountCount > 0 ? " tf-sec-page__status--success" : ""}`}>
                    <div className="tf_checkfill">
                      {connectedAccountCount > 0 ? <i className="ri-check-fill" /> : <i className="ri-close-fill" />}
                    </div>
                  </div>
                </div>
                <div className="tf-sec-page__row-actions">
                  <button type="button" className="tf-sec-page__btn" onClick={() => go("/user_profile/security/accountConnections")}>
                    {connectedAccountCount > 0 ? "Manage" : "Turn on"}
                  </button>
                </div>
              </div> */}
            </div>
          </section>

          <section className="tf-sec-page__card">
            <div className="tf-sec-page__card-header">
              <h2 className="tf-sec-page__card-title">Devices & activity</h2>
            </div>
            <div className="tf-sec-page__card-body">
              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container12.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Authorized Devices</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Manages and recognizes trusted devices for secure account access. Helps prevent unauthorized logins by allowing
                      access only from approved devices.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  <span className="tf-sec-page__status-email">Devices & sessions</span>
                </div>
                <div className="tf-sec-page__row-actions">
                  <button type="button" className="tf-sec-page__btn" onClick={() => go("/user_profile/security/authorizedDevices")}>
                    Manage
                  </button>
                </div>
              </div>

              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Security Logs</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Logs of your login and security setting activity
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  {lastLoginIpMasked ? (
                    <span className="tf-sec-page__status-email">
                      Last login IP: {lastLoginIpMasked}
                    </span>
                  ) : null}
                </div>
                <div className="tf-sec-page__row-actions">
                  <button type="button" className="tf-sec-page__btn" onClick={() => go("/user_profile/security/securityLogs")}>
                    View
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Account Management — temporarily hidden */}
          {false && (
            <section className="tf-sec-page__card">
              <div className="tf-sec-page__card-header">
                <h2 className="tf-sec-page__card-title">Account Management</h2>
              </div>
              <div className="tf-sec-page__card-body">
                <div className="tf-sec-page__row">
                  <div className="tf-sec-page__row-main">
                    <div className="tf-sec-page__row-icon-wrap">
                      <img className="tf-sec-page__row-icon" src="/images/security/Container15.svg" alt="" />
                    </div>
                    <div className="tf-sec-page__row-body">
                      <div className="tf-sec-page__row-heading-line">
                        <span className="tf-sec-page__row-heading">Disable Account</span>
                      </div>
                      <p className="tf-sec-page__row-description">
                        Allows users to temporarily deactivate their account for added security. Prevents access and protects data until
                        the account is re-enabled.
                      </p>
                    </div>
                  </div>
                  <div className="tf-sec-page__row-meta">
                    <div className="tf-sec-page__status">
                      {/* <div className="tf_checkfill"><i className="ri-check-fill"></i></div>
                    <span>off</span> */}
                    </div>
                  </div>
                  <div className="tf-sec-page__row-actions">
                    <button type="button" className="tf-sec-page__btn" onClick={() => go("/user_profile/security/disableAccount")}>
                      Disable
                    </button>
                  </div>
                </div>

                <div className="tf-sec-page__row">
                  <div className="tf-sec-page__row-main">
                    <div className="tf-sec-page__row-icon-wrap">
                      <img className="tf-sec-page__row-icon" src="/images/security/Container.svg" alt="" />
                    </div>
                    <div className="tf-sec-page__row-body">
                      <div className="tf-sec-page__row-heading-line">
                        <span className="tf-sec-page__row-heading">Close Account</span>
                      </div>
                      <p className="tf-sec-page__row-description">
                        Permanently deletes the user account and associated data. Ensures complete removal of access and disables all
                        related services.
                      </p>
                    </div>
                  </div>
                  <div className="tf-sec-page__row-meta">
                    <div className="tf-sec-page__status">
                      {/* <div className="tf_checkfill"><i className="ri-check-fill"></i></div>
                    <span>off</span> */}
                    </div>
                  </div>
                  <div className="tf-sec-page__row-actions">
                    <button type="button" className="tf-sec-page__btn" onClick={() => go("/user_profile/security/closeAccount")}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="tf-sec-page__card">
            <div className="tf-sec-page__card-header">
              <h2 className="tf-sec-page__card-title">Other Settings</h2>
            </div>
            <div className="tf-sec-page__card-body">
              <div className="tf-sec-page__row">
                <div className="tf-sec-page__row-main">
                  <div className="tf-sec-page__row-icon-wrap">
                    <img className="tf-sec-page__row-icon" src="/images/security/Container2.svg" alt="" />
                  </div>
                  <div className="tf-sec-page__row-body">
                    <div className="tf-sec-page__row-heading-line">
                      <span className="tf-sec-page__row-heading">Third Party Account Access Management</span>
                    </div>
                    <p className="tf-sec-page__row-description">
                      Controls and manages access granted to external apps and services. Helps protect account data by allowing users to
                      review and revoke permissions anytime.
                    </p>
                  </div>
                </div>
                <div className="tf-sec-page__row-meta">
                  {/* <span className="tf-sec-page__google-badge">
                    <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                      <path
                        fill="#FFC107"
                        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
                      />
                      <path
                        fill="#FF3D00"
                        d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
                      />
                      <path
                        fill="#4CAF50"
                        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
                      />
                      <path
                        fill="#1976D2"
                        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
                      />
                    </svg>
                  </span> */}
                </div>
                <div className="tf-sec-page__row-actions">
                  <button type="button" className="tf-sec-page__btn" onClick={() => go("/user_profile/security/thirdPartyAccess")}>
                    Change
                  </button>
                </div>
              </div>
            </div>
          </section>

          {isLogin2StepOpen ? (
            <div className="tf-sec-page__l2sv-overlay" role="presentation">
              <div className="tf-sec-page__l2sv-modal" role="dialog" aria-modal="true" aria-labelledby="tf-l2sv-title">
                <button
                  type="button"
                  className="tf-sec-page__l2sv-close"
                  aria-label="Close"
                  onClick={() => setIsLogin2StepOpen(false)}
                >
                  ×
                </button>

                <h2 id="tf-l2sv-title" className="tf-sec-page__l2sv-title">
                  Login 2-Step Verification
                </h2>
                <p className="tf-sec-page__l2sv-subtitle">
                  Once enabled, these methods will be used to verify your identity when you log in.
                </p>

                <div className="tf-sec-page__l2sv-list" role="list">
                  <div className="tf-sec-page__l2sv-item" role="listitem">
                    <div className="tf-sec-page__l2sv-left">
                      <span className="tf-sec-page__l2sv-icon" aria-hidden="true">
                        <img src="/images/security/verification_icon.svg" alt="" />
                      </span>
                      <span className="tf-sec-page__l2sv-copy">
                        <span className="tf-sec-page__l2sv-text">Google Authenticator</span>
                        {!hasGoogleAuth ? (
                          <span className="tf-sec-page__l2sv-hint">Not bound — bind it before using for login</span>
                        ) : null}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={`tf-sec-page__l2sv-switch ${login2Step.googleAuthenticator ? "is-on" : ""}${!hasGoogleAuth ? " is-unbound" : ""}`}
                      role="switch"
                      aria-checked={login2Step.googleAuthenticator}
                      onClick={() => {
                        if (!hasGoogleAuth) {
                          if (login2Step.googleAuthenticator) {
                            setLogin2Step((s) => ({ ...s, googleAuthenticator: false }));
                            setLogin2StepMethods((s) => ({ ...s, totp: false }));
                            alertSuccessMessage("Google Authenticator is already removed. It is no longer used for login.");
                            return;
                          }
                          setIsLogin2StepOpen(false);
                          window.setTimeout(() => openBindPrompt("googleAuth"), 0);
                          return;
                        }
                        if (login2Step.googleAuthenticator) {
                          openGoogleVerify("disable");
                        } else {
                          openGoogleVerify("enable");
                        }
                      }}
                    >
                      <span className="tf-sec-page__l2sv-knob" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="tf-sec-page__l2sv-item" role="listitem">
                    <div className="tf-sec-page__l2sv-left">
                      <span className="tf-sec-page__l2sv-icon" aria-hidden="true">
                        <img src="/images/security/verification_icontwo.svg" alt="" />
                      </span>
                      <span className="tf-sec-page__l2sv-text">SMS verification</span>
                    </div>
                    <button
                      type="button"
                      className={`tf-sec-page__l2sv-switch ${login2Step.smsVerification ? "is-on" : ""}`}
                      role="switch"
                      aria-checked={login2Step.smsVerification}
                      onClick={async () => {
                        if (!hasBoundPhone) {
                          setIsLogin2StepOpen(false);
                          window.setTimeout(() => openBindPrompt("phone"), 0);
                          return;
                        }
                        const action = login2Step.smsVerification ? "disable" : "enable";
                        const otpSent = await sendOtpForTwoLogin2Step("phone");
                        if (!otpSent) return;
                        openSmsVerify(action);
                      }}
                    >
                      <span className="tf-sec-page__l2sv-knob" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="tf-sec-page__l2sv-item" role="listitem">
                    <div className="tf-sec-page__l2sv-left">
                      <span className="tf-sec-page__l2sv-icon" aria-hidden="true">
                        <img src="/images/security/verification_icon3.svg" alt="" />
                      </span>
                      <span className="tf-sec-page__l2sv-text">Email verification</span>
                    </div>
                    <button
                      type="button"
                      className={`tf-sec-page__l2sv-switch ${login2Step.emailVerification ? "is-on" : ""}`}
                      role="switch"
                      aria-checked={login2Step.emailVerification}
                      onClick={async () => {
                        const otpSent = await sendOtpForTwoLogin2Step("email");
                        if (!otpSent) return;
                        if (login2Step.emailVerification) {
                          openEmailVerify("login", "disable");
                        } else {
                          openEmailVerify("login", "enable");
                        }
                      }}
                    >
                      <span className="tf-sec-page__l2sv-knob" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {googleVerifyOpen ? (
            <div className="passkey-modal-overlay" role="presentation" onClick={closeGoogleVerify}>
              <div
                className="passkey-modal passkey-verify-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ga-verify-title"
                onClick={(e) => e.stopPropagation()}
              >
                <button type="button" className="passkey-modal__close" aria-label="Close" onClick={closeGoogleVerify}>
                  ×
                </button>
                <h2 id="ga-verify-title" className="passkey-verify-modal__title">
                  Verify Your Google Authenticator
                </h2>
                <p className="passkey-verify-modal__subtitle">Enter the Google Authenticator code</p>

                <div className="passkey-verify-modal__otpRow" aria-label="Verification code">
                  {googleOtp.map((d, idx) => (
                    <input
                      // eslint-disable-next-line react/no-array-index-key
                      key={idx}
                      id={`ga-otp-${idx}`}
                      className="passkey-verify-modal__otp"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      autoComplete={idx === 0 ? "one-time-code" : undefined}
                      aria-label={`Digit ${idx + 1}`}
                      value={d}
                      onChange={(e) => handleGoogleOtpChange(idx, e)}
                      onKeyDown={(e) => handleGoogleOtpKeyDown(idx, e)}
                    />
                  ))}
                </div>

                <div className="passkey-verify-modal__links passkey-verify-modal__links--totp">
                  {/* <button type="button" className="passkey-verify-modal__link">
                    Resend
                  </button> */}
                  <button
                    type="button"
                    className="passkey-verify-modal__link passkey-verify-modal__link--paste"
                    onClick={pasteGoogleOtp}
                  >
                    Paste <i className="ri-file-copy-fill" aria-hidden="true" />
                  </button>
                </div>

                <button
                  type="button"
                  className="passkey-verify-modal__confirm"
                  disabled={!googleOtpComplete}
                  onClick={confirmGoogleOtp}
                  style={googleOtpComplete ? { cursor: "pointer" } : undefined}
                >
                  Confirm
                </button>

                <button type="button" className="passkey-verify-modal__unable" onClick={() => go("/asset_managemnet/security_verification_unavailable")}>
                  Security verification unavailable?
                </button>
              </div>
            </div>
          ) : null}

          {smsVerifyOpen ? (
            <div className="passkey-modal-overlay" role="presentation" onClick={closeSmsVerify}>
              <div
                className="passkey-modal passkey-verify-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="sms-verify-title"
                onClick={(e) => e.stopPropagation()}
              >
                <button type="button" className="passkey-modal__close" aria-label="Close" onClick={closeSmsVerify}>
                  ×
                </button>
                <h2 id="sms-verify-title" className="passkey-verify-modal__title">
                  Verify Your Mobile
                </h2>
                <p className="passkey-verify-modal__subtitle">Enter the verification code</p>

                <div className="passkey-verify-modal__otpRow" aria-label="Verification code">
                  {smsOtp.map((d, idx) => (
                    <input
                      // eslint-disable-next-line react/no-array-index-key
                      key={idx}
                      id={`sms-otp-${idx}`}
                      className="passkey-verify-modal__otp"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      autoComplete={idx === 0 ? "one-time-code" : undefined}
                      aria-label={`Digit ${idx + 1}`}
                      value={d}
                      onChange={(e) => handleSmsOtpChange(idx, e)}
                      onKeyDown={(e) => handleSmsOtpKeyDown(idx, e)}
                    />
                  ))}
                </div>

                <div className="passkey-verify-modal__links">
                  <button
                    type="button"
                    className="passkey-verify-modal__link passkey-verify-modal__link--paste"
                    onClick={pasteSmsOtp}
                  >
                    Paste <i className="ri-file-copy-fill" aria-hidden="true" />
                  </button>
                </div>

                <button
                  type="button"
                  className="passkey-verify-modal__confirm"
                  disabled={!smsOtpComplete}
                  onClick={confirmSmsOtp}
                  style={smsOtpComplete ? { cursor: "pointer" } : undefined}
                >
                  Confirm
                </button>
                <button type="button" className="passkey-verify-modal__unable" onClick={() => go("/asset_managemnet/security_verification_unavailable")}>
                  Security verification unavailable?
                </button>
              </div>
            </div>
          ) : null}

          <AntiPhishingRemoveVerification
            open={antiPhishingRemoveOpen}
            onClose={closeAntiPhishingRemove}
            userDetails={userDetails}
            onRemoved={fetchAntiPhishingStatus}
          />

          {isDisableGAOpen ? (
            <div className="passkey-modal-overlay disable_ga_overlay" role="presentation" onClick={closeDisableGA}>
              <div
                className="passkey-modal passkey-verify-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ga-disable-title"
                onClick={(e) => e.stopPropagation()}
              >
                <button type="button" className="passkey-modal__close" aria-label="Close" onClick={closeDisableGA}>
                  ×
                </button>
                <h2 id="ga-disable-title" className="passkey-verify-modal__title">
                  Disable Google Authenticator
                </h2>
                <p className="passkey-verify-modal__subtitle">
                  {getDisableGASubtitle()}
                </p>

                {disableGAMethodsPickerOpen ? (
                  <div className="tf-sec-page__method-list" role="list">
                    {disableGAAvailableMethods.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        role="listitem"
                        className={`tf-sec-page__btn ${disableGAMethod === m.id ? "active" : ""}`}
                        onClick={() => selectDisableGAMethod(m.id)}
                      >
                        <strong>{m.label}</strong>
                        <div className="tf-sec-page__btn-desc">{m.description}</div>
                      </button>
                    ))}
                  </div>
                ) : null}

                {!disableGAMethodsPickerOpen && disableGAMethod === "passkey" ? (
                  <button
                    type="button"
                    className="passkey-verify-modal__confirm"
                    disabled={isDisablingGA || isPasskeyVerifying}
                    onClick={handleConfirmDisableGA}
                    style={!isDisablingGA && !isPasskeyVerifying ? { cursor: "pointer" } : undefined}
                  >
                    {isDisablingGA || isPasskeyVerifying ? "Verifying…" : "Verify with Passkey"}
                  </button>
                ) : null}

                {!disableGAMethodsPickerOpen && disableGAMethod !== "passkey" ? (
                  <>
                    <div className="passkey-verify-modal__otpRow" aria-label="Verification code">
                      {disableGAOtp.map((d, idx) => (
                        <input
                          // eslint-disable-next-line react/no-array-index-key
                          key={idx}
                          id={`ga-disable-otp-${idx}`}
                          className="passkey-verify-modal__otp"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={1}
                          autoComplete={idx === 0 ? "one-time-code" : undefined}
                          aria-label={`Digit ${idx + 1}`}
                          value={d}
                          onChange={(e) => handleDisableGAOtpChange(idx, e)}
                          onKeyDown={(e) => handleDisableGAOtpKeyDown(idx, e)}
                        />
                      ))}
                    </div>

                    <div className="passkey-verify-modal__links passkey-verify-modal__links--totp">
                      {/* {(disableGAMethod === "email" || disableGAMethod === "mobile") ? (
                        <button
                          type="button"
                          className="passkey-verify-modal__link"
                          disabled={resendTimer > 0 || isLoading || isDisablingGA}
                          onClick={sendDisableGAOtp}
                        >
                          {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Send code"}
                        </button>
                      ) : null} */}
                      <button
                        type="button"
                        className="passkey-verify-modal__link passkey-verify-modal__link--paste"
                        onClick={pasteDisableGAOtp}
                      >
                        Paste <i className="ri-file-copy-fill" aria-hidden="true" />
                      </button>
                    </div>

                    <button
                      type="button"
                      className="passkey-verify-modal__confirm"
                      disabled={!disableGAOtpComplete || isDisablingGA}
                      onClick={handleConfirmDisableGA}
                      style={disableGAOtpComplete && !isDisablingGA ? { cursor: "pointer" } : undefined}
                    >
                      {isDisablingGA ? "Disabling…" : "Confirm"}
                    </button>
                  </>
                ) : null}

                {!disableGAMethodsPickerOpen && disableGAAvailableMethods.length > 1 ? (
                  <button
                    type="button"
                    className="passkey-verify-modal__unable"
                    style={{ marginTop: 12 }}
                    onClick={() => setDisableGAMethodsPickerOpen(true)}
                  >
                    Use another verification method
                  </button>
                ) : null}
                <button
                  type="button"
                  className="passkey-verify-modal__unable"
                  style={{ marginTop: 12 }}
                  onClick={() =>
                    navigate("/asset_managemnet/security_verification_unavailable", {
                      state: { preselectMethods: ["authApp"] },
                    })
                  }
                >
                  Security verification unavailable?
                </button>
              </div>
            </div>
          ) : null}

          {gaRemovedNotice.open ? (
            <div
              className="tf-sec-page__unbound-overlay"
              role="presentation"
              onClick={() => setGaRemovedNotice({ open: false, hours: 24 })}
            >
              <div
                className="tf-sec-page__unbound-modal tf-sec-page__removed-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tf-ga-removed-title"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="tf-sec-page__unbound-close"
                  aria-label="Close"
                  onClick={() => setGaRemovedNotice({ open: false, hours: 24 })}
                >
                  ×
                </button>
                <div className="tf-sec-page__removed-hero" aria-hidden="true">
                  <img src="/images/sucessfull_payment_icon.svg" alt="" />
                </div>
                <h2 id="tf-ga-removed-title" className="tf-sec-page__unbound-title">
                  Google Authenticator removed
                </h2>
                <p className="tf-sec-page__unbound-subtitle">
                  It is no longer used for login. Withdrawals, P2P selling, and payments may be paused for{" "}
                  {gaRemovedNotice.hours} hours to protect your account.
                </p>
                <button
                  type="button"
                  className="tf-sec-page__unbound-cta"
                  onClick={() => setGaRemovedNotice({ open: false, hours: 24 })}
                >
                  Done
                </button>
              </div>
            </div>
          ) : null}

          {bindPrompt.open && typeof document !== "undefined"
            ? createPortal(
              <div
                className="tf-sec-page__unbound-overlay tf-sec-page__unbound-overlay--portal"
                role="presentation"
                onClick={() => setBindPrompt((s) => ({ ...s, open: false }))}
              >
                <div
                  className="tf-sec-page__unbound-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="tf-phone-unbound-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="tf-sec-page__unbound-close"
                    aria-label="Close"
                    onClick={() => setBindPrompt((s) => ({ ...s, open: false }))}
                  >
                    ×
                  </button>
                  <h2 id="tf-phone-unbound-title" className="tf-sec-page__unbound-title">
                    {bindPrompt.title}
                  </h2>
                  <p className="tf-sec-page__unbound-subtitle">
                    {bindPrompt.description}
                  </p>
                  <button
                    type="button"
                    className="tf-sec-page__unbound-cta"
                    onClick={() => {
                      setBindPrompt((s) => ({ ...s, open: false }));
                      setIsLogin2StepOpen(false);
                      setIsWithdrawSettingsOpen(false);
                      go(bindPrompt.route);
                    }}
                  >
                    Bind now
                  </button>
                </div>
              </div>,
              document.body
            )
            : null}

          {emailVerifyOpen ? (
            <div className="passkey-modal-overlay" role="presentation" onClick={closeEmailVerify}>
              <div
                className="passkey-modal passkey-verify-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ev-verify-title"
                onClick={(e) => e.stopPropagation()}
              >
                <button type="button" className="passkey-modal__close" aria-label="Close" onClick={closeEmailVerify}>
                  ×
                </button>
                <h2 id="ev-verify-title" className="passkey-verify-modal__title">
                  Verify Your Email
                </h2>
                <p className="passkey-verify-modal__subtitle">
                  Please enter the 6-digit verification code sent to {maskedEmail}, which is valid for 10 minutes.
                </p>

                <div className="passkey-verify-modal__otpRow" aria-label="Verification code">
                  {emailOtp.map((d, idx) => (
                    <input
                      // eslint-disable-next-line react/no-array-index-key
                      key={idx}
                      id={`ev-otp-${idx}`}
                      className="passkey-verify-modal__otp"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      autoComplete={idx === 0 ? "one-time-code" : undefined}
                      aria-label={`Digit ${idx + 1}`}
                      value={d}
                      onChange={(e) => handleEmailOtpChange(idx, e)}
                      onKeyDown={(e) => handleEmailOtpKeyDown(idx, e)}
                    />
                  ))}
                </div>

                <div className="passkey-verify-modal__links">
                  <button
                    type="button"
                    className="passkey-verify-modal__link"
                    disabled={emailResendLeft > 0}
                    style={emailResendLeft > 0 ? { opacity: 0.6, cursor: "not-allowed", textDecoration: "none" } : undefined}
                    onClick={async () => {
                      if (emailResendLeft > 0) return;
                      if (emailVerifyTarget === "wds") {
                        sendWithdrawalVerificationOtp("email").catch(() => { });
                        setEmailResendLeft(60);
                        return;
                      }
                      if (emailVerifyTarget === "login") {
                        const ok = await sendOtpForLogin2Step("email");
                        if (ok) setEmailResendLeft(60);
                        return;
                      }
                      setEmailResendLeft(60);
                    }}
                  >
                    {emailResendLeft > 0 ? `Resend in ${emailResendLeft}s` : "Resend"}
                  </button>
                  <button
                    type="button"
                    className="passkey-verify-modal__link passkey-verify-modal__link--paste"
                    onClick={pasteEmailOtp}
                  >
                    Paste <i className="ri-file-copy-fill" aria-hidden="true" />
                  </button>
                </div>

                <button
                  type="button"
                  className="passkey-verify-modal__confirm"
                  disabled={!emailOtpComplete}
                  onClick={confirmEmailOtp}
                  style={emailOtpComplete ? { cursor: "pointer" } : undefined}
                >
                  Confirm
                </button>

                <button type="button" className="passkey-verify-modal__unable" onClick={() => go("/asset_managemnet/security_verification_unavailable")}>
                  Security verification unavailable?
                </button>
              </div>
            </div>
          ) : null}

          {fundPasswordChangeConfirmOpen ? (
            <div
              className="tf-sec-page__fundpw-overlay"
              role="presentation"
              onClick={() => setFundPasswordChangeConfirmOpen(false)}
            >
              <div
                className="tf-sec-page__fpw-confirm-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tf-fpw-confirm-title"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="tf-sec-page__fpw-confirm-close"
                  aria-label="Close"
                  onClick={() => setFundPasswordChangeConfirmOpen(false)}
                >
                  ×
                </button>
                <div className="tf-sec-page__fpw-confirm-art" aria-hidden="true">
                  {/* <span className="tf-sec-page__fpw-confirm-badge tf-sec-page__fpw-confirm-badge--lime" /> */}
                  {/* <span className="tf-sec-page__fpw-confirm-badge tf-sec-page__fpw-confirm-badge--blue" /> */}
                  <img
                    className="tf-sec-page__fpw-confirm-shield dark_img"
                    src="/images/security/fund-password.svg"
                    alt=""
                  />
                  <img
                    className="tf-sec-page__fpw-confirm-shield light_img"
                    src="/images/security/fund-password_light.svg"
                    alt=""
                  />
                </div>
                <h2 id="tf-fpw-confirm-title" className="tf-sec-page__fpw-confirm-title">
                  {effectiveHasFundPassword
                    ? "Are you sure you want to change the fund password?"
                    : "Are you sure you want to set your fund password?"}
                </h2>
                <p className="tf-sec-page__fpw-confirm-body">
                  For the security of your assets, withdrawals and P2P selling will be temporarily locked for{" "}
                  <strong className="tf-sec-page__fpw-confirm-hours">24</strong> hours after you set or change your fund
                  password.
                </p>
                <div className="tf-sec-page__fpw-confirm-actions">
                  <button
                    type="button"
                    className="tf-sec-page__fpw-confirm-cancel"
                    onClick={() => setFundPasswordChangeConfirmOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="tf-sec-page__fpw-confirm-ok"
                    onClick={() => {
                      setFundPasswordChangeConfirmOpen(false);
                      go("/user_profile/security/setFundPassword");
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {fundPasswordMissingOpen ? (
            <div className="tf-sec-page__fundpw-overlay" role="presentation" onClick={() => setFundPasswordMissingOpen(false)}>
              <div
                className="tf-sec-page__fundpw-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tf-fundpw-title"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="tf-sec-page__fundpw-close"
                  aria-label="Close"
                  onClick={() => setFundPasswordMissingOpen(false)}
                >
                  ×
                </button>
                <h2 id="tf-fundpw-title" className="tf-sec-page__fundpw-title">
                  No fund password has been set
                </h2>
                <p className="tf-sec-page__fundpw-subtitle">
                  You need to set the fund password first to use it for withdrawal verification.
                </p>
                <button
                  type="button"
                  className="tf-sec-page__fundpw-cta"
                  onClick={() => {
                    setFundPasswordMissingOpen(false);
                    setIsWithdrawSettingsOpen(false);
                    go("/user_profile/security/setFundPassword");
                  }}
                >
                  Set now
                </button>
              </div>
            </div>
          ) : null}

          {wdsFundPwOpen ? (
            <div className="passkey-modal-overlay" role="presentation" onClick={() => { setWdsFundPwOpen(false); setWdsPendingToggle(null); }}>
              <div className="passkey-modal passkey-verify-modal" role="dialog" aria-modal="true" aria-labelledby="wds-fpw-title" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="passkey-modal__close" aria-label="Close" onClick={() => { setWdsFundPwOpen(false); setWdsPendingToggle(null); }}>×</button>
                <h2 id="wds-fpw-title" className="passkey-verify-modal__title">Verify Fund Password</h2>
                <p className="passkey-verify-modal__subtitle">Enter your fund password to {wdsPendingToggle?.enable ? "enable" : "disable"} fund password withdrawal verification.</p>
                <input
                  type="password"
                  className="passkey-verify-modal__otp"
                  style={{ width: "100%", textAlign: "left", letterSpacing: "normal", fontSize: 14, padding: "10px 14px", marginBottom: 6 }}
                  placeholder="Fund password"
                  value={wdsFundPwInput}
                  onChange={(e) => { setWdsFundPwInput(e.target.value); setWdsFundPwError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") submitWdsFundPw(); }}
                  autoFocus
                />
                {wdsFundPwError && <p style={{ color: "#dc2626", fontSize: 12, margin: "4px 0 8px" }}>{wdsFundPwError}</p>}
                <button type="button" className="passkey-verify-modal__confirm" disabled={!wdsFundPwInput.trim()} onClick={submitWdsFundPw} style={wdsFundPwInput.trim() ? { cursor: "pointer" } : undefined}>
                  Confirm
                </button>
              </div>
            </div>
          ) : null}

          {trustedAddrMissingOpen ? (
            <div className="tf-sec-page__fundpw-overlay" role="presentation" onClick={() => setTrustedAddrMissingOpen(false)}>
              <div
                className="tf-sec-page__fundpw-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tf-trustedaddr-title"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="tf-sec-page__fundpw-close"
                  aria-label="Close"
                  onClick={() => setTrustedAddrMissingOpen(false)}
                >
                  ×
                </button>
                <h2 id="tf-trustedaddr-title" className="tf-sec-page__fundpw-title">
                  No trusted address has been set
                </h2>
                <p className="tf-sec-page__fundpw-subtitle">
                  You need to add a trusted address first to use it for withdrawal verification.
                </p>
                <button
                  type="button"
                  className="tf-sec-page__fundpw-cta"
                  onClick={() => {
                    setTrustedAddrMissingOpen(false);
                    setIsWithdrawSettingsOpen(false);
                    go("/user_profile/withdraw");
                  }}
                >
                  Set now
                </button>
              </div>
            </div>
          ) : null}

          {isWithdrawSettingsOpen ? (
            <div className="tf-sec-page__wds-overlay" role="presentation">
              <div className="tf-sec-page__wds-modal" role="dialog" aria-modal="true" aria-labelledby="tf-wds-title">
                <button
                  type="button"
                  className="tf-sec-page__wds-close"
                  aria-label="Close"
                  onClick={() => setIsWithdrawSettingsOpen(false)}
                >
                  ×
                </button>

                <h2 id="tf-wds-title" className="tf-sec-page__wds-title">
                  Withdrawal Settings
                </h2>
                <p className="tf-sec-page__wds-subtitle">Manage security verification for asset withdrawals.</p>


                <div className="tf-sec-page__wds-list" role="list">
                  {/* ── Email verification ── */}
                  <div className="tf-sec-page__wds-item" role="listitem">
                    <div className="tf-sec-page__wds-left">
                      <span className="tf-sec-page__wds-icon" aria-hidden="true">
                        <img src="/images/security/setting_icon3.svg" alt="" />
                      </span>
                      <span className="tf-sec-page__wds-text">Email verification</span>
                    </div>
                    <button
                      type="button"
                      className={`tf-sec-page__wds-switch ${withdrawSettings.emailVerification ? "is-on" : ""}`}
                      role="switch"
                      aria-checked={withdrawSettings.emailVerification}
                      onClick={() => handleWdsToggle("email", !withdrawSettings.emailVerification)}
                    >
                      <span className="tf-sec-page__wds-knob" aria-hidden="true" />
                    </button>
                  </div>

                  {/* ── SMS verification ── */}
                  <div className="tf-sec-page__wds-item" role="listitem">
                    <div className="tf-sec-page__wds-left">
                      <span className="tf-sec-page__wds-icon" aria-hidden="true">
                        <img src="/images/security/setting_icon.svg" alt="" />
                      </span>
                      <span className="tf-sec-page__wds-text">SMS verification</span>
                    </div>
                    <button
                      type="button"
                      className={`tf-sec-page__wds-switch ${withdrawSettings.smsVerification ? "is-on" : ""}`}
                      role="switch"
                      aria-checked={withdrawSettings.smsVerification}
                      onClick={() => {
                        if (!withdrawSettings.smsVerification && (!hasBoundPhone || !hasMobile)) {
                          setIsWithdrawSettingsOpen(false);
                          window.setTimeout(() => openBindPrompt("phone"), 0);
                          return;
                        }
                        handleWdsToggle("mobile", !withdrawSettings.smsVerification);
                      }}
                    >
                      <span className="tf-sec-page__wds-knob" aria-hidden="true" />
                    </button>
                  </div>

                  {/* ── Google Authenticator ── */}
                  <div className="tf-sec-page__wds-item" role="listitem">
                    <div className="tf-sec-page__wds-left">
                      <span className="tf-sec-page__wds-icon" aria-hidden="true">
                        <img src="/images/security/setting_icon2.svg" alt="" />
                      </span>
                      <span className="tf-sec-page__wds-text">Google Authenticator</span>
                    </div>
                    <button
                      type="button"
                      className={`tf-sec-page__wds-switch ${withdrawSettings.googleAuthenticator ? "is-on" : ""}`}
                      role="switch"
                      aria-checked={withdrawSettings.googleAuthenticator}
                      onClick={() => {
                        if (!withdrawSettings.googleAuthenticator && !hasGoogleAuth) {
                          setIsWithdrawSettingsOpen(false);
                          go("/user_profile/security/bindGoogleAuthenticator");
                          return;
                        }
                        handleWdsToggle("google_authenticator", !withdrawSettings.googleAuthenticator);
                      }}
                    >
                      <span className="tf-sec-page__wds-knob" aria-hidden="true" />
                    </button>
                  </div>

                  {/* ── Fund Password ── */}
                  <div className="tf-sec-page__wds-item" role="listitem">
                    <div className="tf-sec-page__wds-left">
                      <span className="tf-sec-page__wds-icon" aria-hidden="true">
                        <img src="/images/security/setting_icon2.svg" alt="" />
                      </span>
                      <span className="tf-sec-page__wds-text">Fund Password</span>
                    </div>
                    <button
                      type="button"
                      className={`tf-sec-page__wds-switch ${withdrawSettings.fundPassword ? "is-on" : ""}`}
                      role="switch"
                      aria-checked={withdrawSettings.fundPassword}
                      onClick={() => {
                        if (!withdrawSettings.fundPassword && !effectiveHasFundPassword) {
                          setFundPasswordMissingOpen(true);
                          return;
                        }
                        handleWdsToggle("fund_password", !withdrawSettings.fundPassword);
                      }}
                    >
                      <span className="tf-sec-page__wds-knob" aria-hidden="true" />
                    </button>
                  </div>

                  {/* ── Trusted Addresses (UI-only) ── */}
                  <div className="tf-sec-page__wds-item" role="listitem">
                    <div className="tf-sec-page__wds-left">
                      <span className="tf-sec-page__wds-icon" aria-hidden="true">
                        <img src="/images/security/setting_icon4.svg" alt="" />
                      </span>
                      <span className="tf-sec-page__wds-text">Withdraw Only to Trusted Addresses</span>
                    </div>
                    <button
                      type="button"
                      className={`tf-sec-page__wds-switch ${withdrawSettings.trustedAddresses ? "is-on" : ""}`}
                      role="switch"
                      aria-checked={withdrawSettings.trustedAddresses}
                      onClick={() => {
                        if (!withdrawSettings.trustedAddresses && !hasTrustedAddresses) {
                          setTrustedAddrMissingOpen(true);
                          return;
                        }
                        setWithdrawSettings((s) => ({ ...s, trustedAddresses: !withdrawSettings.trustedAddresses }));
                      }}
                    >
                      <span className="tf-sec-page__wds-knob" aria-hidden="true" />
                    </button>
                  </div>

                  {/* <button type="button" className="tf-sec-page__wds-item tf-sec-page__wds-item--link">
                    <div className="tf-sec-page__wds-left">
                      <span className="tf-sec-page__wds-icon" aria-hidden="true">
                        <img src="/images/security/Icon.svg" alt="" />
                      </span>
                      <span className="tf-sec-page__wds-text">API Withdrawal Settings</span>
                    </div>
                    <span className="tf-sec-page__wds-right">
                      <span className="tf-sec-page__wds-muted">More options</span>
                      <span className="tf-sec-page__wds-chev" aria-hidden="true">
                        ›
                      </span>
                    </span>
                  </button> */}
                </div>

                {/* <div className="tf-sec-page__wds-radioGroup" role="radiogroup" aria-label="Withdrawal address options">
                  <button
                    type="button"
                    className={`tf-sec-page__wds-radioRow ${withdrawAddressMode === "addressBook" ? "is-selected" : ""}`}
                    onClick={() => setWithdrawAddressMode("addressBook")}
                  >
                    <span className="tf-sec-page__wds-radio" aria-hidden="true" />
                    <span className="tf-sec-page__wds-radioText">To Address Book Addresses Only</span>
                  </button>
                  <button
                    type="button"
                    className={`tf-sec-page__wds-radioRow ${withdrawAddressMode === "verificationFree" ? "is-selected" : ""}`}
                    onClick={() => setWithdrawAddressMode("verificationFree")}
                  >
                    <span className="tf-sec-page__wds-radio" aria-hidden="true" />
                    <span className="tf-sec-page__wds-radioText">Verification-free addresses only</span>
                  </button>
                </div> */}
              </div>
            </div>
          ) : null}

        </div>
      </div>
    </>
  );
};

export default TwofactorPage;
